import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import api from "services/api";
import { usePageTitle } from "hooks/usePageTitle";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
  Stack,
  Button,
  Menu,
  MenuItem,
  ListSubheader,
  ListItemIcon,
  ListItemText,
  Divider,
  Popover,
  Popper,
  Paper,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@mui/material";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import {
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import { loadExcelJS } from "helpers/loadExcelJS";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  buildEmailThreadLink,
  formatDurationToHHMM,
  formatMileage,
  formatTimeToHHMM12Hr,
  getDateProximityColor,
  getProjectNumber,
  getStatusColor,
  normalizePhoneNumber,
  numericComparator,
  parseEmailThreadIds,
} from "helpers/ServiceHelpers";
import {
  getWorstCheckSeverity,
  loadValidationCheckConfig,
  runInterpretationChecks,
} from "helpers/serviceValidationChecks";
import ServiceChecksIndicator from "components/ServiceChecksIndicator";
import ChecksSeverityBadge from "components/ChecksSeverityBadge";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
// 2026-10-08 — mini profile side panel section icons
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import ContactPhoneRoundedIcon from "@mui/icons-material/ContactPhoneRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
// 2026-10-13 (Amine) — footer ⋮ menu mirrors the Assignment Profile's
// right-column Actions panel (same sections, labels, icons, gates).
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ForwardToInboxRoundedIcon from "@mui/icons-material/ForwardToInboxRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CONST } from "constants/CONST";
import CEQuickFilter from "components/CEQuickFilter";
import DataGridResponsive from "components/DataGridResponsive";
import QuickFilterField from "components/QuickFilterField";
import { HtmlTooltip } from "components/HeatMap";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useWebSocket } from "contexts/WebSocketContext";
import { NAVBAR_HEIGHT } from "constants/layout";

// Batch C wave 4 — Interpretations columns whose desktop header uses
// ColumnDateFilter (hierarchical date filter) instead of ColumnFilter. Used to
// build the card-mode filter-sheet entries with identical semantics.
const CARD_MODE_DATE_FILTER_FIELDS = ["Date", "CheckedOn"];

// Batch C wave 4 — the desktop headers of these six columns read their filter
// data from the cross-filtering `filterData` array (index = position in
// `filterColumns`); every other header passes `listOfServices` directly.
const CARD_MODE_FILTER_DATA_FIELDS = [
  "ProjectNumber",
  "Date",
  "Client",
  "Requestor",
  "TimeStart",
  "TimeEnd",
];

// 2026-10-08, emptied 2026-10-13 — columns skipped by the card-mode filter
// sheet (which maps over every visible column). Now empty: the Checks column
// is filterable (Passed / Warnings / Errors via its ColumnFilter), so no
// column opts out. Kept as the mechanism for any future informational
// column that carries no filterable value list.
const CARD_MODE_NON_FILTER_FIELDS = [];

// ── 2026-10-08 — Mini profile side panel building blocks ────────────────────
// Width constraints + persistence for the resizable docked panel.
const SIDE_PANEL_MIN_WIDTH = 320;
const SIDE_PANEL_MAX_WIDTH = 760;
const SIDE_PANEL_WIDTH_STORAGE_KEY = "INTERPRETATIONS_SIDE_PANEL_WIDTH";
const clampSidePanelWidth = (width) =>
  Math.min(SIDE_PANEL_MAX_WIDTH, Math.max(SIDE_PANEL_MIN_WIDTH, width));

// Compact section header (tinted icon bubble + title) + key/value rows for
// the desktop single-click panel.
const SidePanelSection = ({ title, icon, children }) => (
  <Box sx={{ px: 2, py: 1.25 }}>
    <Stack direction="row" spacing={1} alignItems="center">
      {icon && (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
            bgcolor: (theme) => `${theme.palette.primary.main}14`,
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        sx={{
          color: "text.primary",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 700,
          fontSize: "0.95rem",
        }}
      >
        {title}
      </Typography>
    </Stack>
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Box>
);

// 2026-10-12 (Amine) — inline quick-action buttons for side panel rows:
// small muted icon buttons (call / text / email / open profile) rendered
// after the value. Callers pass [] when the data is missing so no dead
// buttons ever render.
const SidePanelActions = ({ actions }) => {
  if (!actions || actions.length === 0) return null;
  return (
    <Stack
      direction="row"
      spacing={0.25}
      alignItems="center"
      sx={{ flexShrink: 0, alignSelf: "center" }}
    >
      {actions.map((action) => (
        <Tooltip key={action.key} title={action.label}>
          <IconButton
            size="small"
            aria-label={action.label}
            onClick={action.onClick}
            sx={{ p: "2px", color: "text.secondary" }}
          >
            <action.Icon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ))}
    </Stack>
  );
};

// tel: / sms: / mailto: trio shared by the Linguist, Requestor, Invoice To and
// Contact Persons rows. `phone` arrives normalized (normalizePhoneNumber) and
// may be "N/A"; the digits-only stripping mirrors InterpretationProfile's
// buildCallLink so the link stays dialable whatever the display formatting.
const buildDirectContactActions = (phone, email) => {
  const actions = [];
  const dialable =
    phone && phone !== "N/A" ? String(phone).replace(/[^\d+]/g, "") : "";
  if (dialable) {
    actions.push({
      key: "call",
      label: "Call",
      Icon: CallRoundedIcon,
      onClick: () => {
        window.location.href = `tel:${dialable}`;
      },
    });
    actions.push({
      key: "text",
      label: "Text",
      Icon: SmsRoundedIcon,
      onClick: () => {
        window.location.href = `sms:${dialable}`;
      },
    });
  }
  const normalizedEmail = typeof email === "string" ? email.trim() : "";
  if (normalizedEmail && normalizedEmail.toLowerCase() !== "n/a") {
    actions.push({
      key: "email",
      label: "Email",
      Icon: EmailRoundedIcon,
      onClick: () => {
        window.location.href = `mailto:${normalizedEmail}`;
      },
    });
  }
  return actions;
};

// 2026-10-12 (Amine) — quick-action phone resolution, lifted to module scope
// (2026-10-13) so the side panel's quick actions and the footer ⋮ menu share
// one source. Phones are normalized here (the single formatting source);
// PhoneNumber is a VIRTUAL on Linguist/Requestor composed from the
// PhoneNumbers association, so prefer the association rows when the payload
// carries them (same fallback shape as phoneOf() in InterpretationOutreach
// and the context menu's call-requestor helper), then the flat PhoneNumber
// field. Returns null when nothing usable exists so no dead action buttons
// render.
const resolveRecordPhone = (record) => {
  if (!record) return null;
  const phones = Array.isArray(record.PhoneNumbers)
    ? record.PhoneNumbers.filter((p) => String(p?.Number || "").trim())
    : [];
  if (phones.length > 0) {
    const primary = phones.find((p) => p.IsPrimary) || phones[0];
    const withPrefix =
      String(primary?.CountryCode || "") &&
      String(primary.CountryCode) !== "1"
        ? `+${primary.CountryCode}${primary.Number}`
        : primary.Number;
    const normalized = normalizePhoneNumber(withPrefix);
    return normalized !== "N/A" ? normalized : null;
  }
  const normalized = normalizePhoneNumber(record.PhoneNumber);
  return normalized !== "N/A" ? normalized : null;
};

// 2026-10-12 (Amine) — Google Maps search deep link for a free-text address
// (keyless universal link; opens the maps app on mobile).
const buildMapsSearchUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    String(address || "").trim(),
  )}`;

// Copy the address to the clipboard and confirm via the screen's snackbar
// (success / info pattern already used on this screen). Local helper — kept
// per-file on purpose (no shared cross-screen component).
const copyAddressToClipboard = (address, showSnackbar, theme) => {
  const write = async () => {
    try {
      await navigator.clipboard.writeText(String(address).trim());
      showSnackbar("Address copied", theme, "success");
    } catch {
      showSnackbar("Couldn't copy the address", theme, "info");
    }
  };
  write();
};

// 2026-10-12 (Amine) — two-column grid for the short fact rows (Schedule,
// Client Data, Costs, Rates, Status & Record). Compact cells use stacked
// label-above-value because at the 320px minimum panel width a half-width
// cell (~130px) cannot hold SidePanelField's fixed 104px label column.
const SidePanelFieldGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      columnGap: 2,
      rowGap: 0.25,
    }}
  >
    {children}
  </Box>
);

// Full-width cell inside a SidePanelFieldGrid (e.g. Cancellation Reason).
const SidePanelGridFullRow = ({ children }) => (
  <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>{children}</Box>
);

// Compact grid-cell variant of SidePanelField: small caption label with the
// value line below it. Same N/A fallback and overflow rules as SidePanelField.
const SidePanelCompactField = ({ label, value, actions = null }) => (
  <Box sx={{ py: 0.5, minWidth: 0 }}>
    <Typography
      component="div"
      sx={{ color: "text.secondary", fontSize: "0.72rem", lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        mt: "2px",
      }}
    >
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography
          component="div"
          sx={{
            minWidth: 0,
            overflowWrap: "anywhere",
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.35,
          }}
        >
          {value === "" || value === undefined || value === null
            ? "N/A"
            : value}
        </Typography>
      )}
      <SidePanelActions actions={actions} />
    </Box>
  </Box>
);

// A two-column label/value row. `value` may be a React node (e.g. the
// clamped Details block); plain strings fall back to "N/A" when empty.
// `actions` renders small icon buttons after the value (same line).
const SidePanelField = ({ label, value, multiline = false, actions = null }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: multiline ? "flex-start" : "baseline",
      gap: 1,
      py: 0.5,
    }}
  >
    <Typography
      component="span"
      sx={{
        color: "text.secondary",
        flexShrink: 0,
        width: 104,
        fontSize: "0.8125rem",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography
          component="div"
          sx={{
            overflowWrap: "anywhere",
            whiteSpace: multiline ? "pre-wrap" : "normal",
            fontSize: "0.9375rem",
            fontWeight: 500,
            color: "text.primary",
            lineHeight: 1.45,
          }}
        >
          {value === "" || value === undefined || value === null
            ? "N/A"
            : value}
        </Typography>
      )}
    </Box>
    <SidePanelActions actions={actions} />
  </Box>
);

const Interpretations = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.tz.setDefault("America/New_York");

  usePageTitle("Interpretations");
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const navigate = useNavigate();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const [isLoading, setIsLoading] = useState(true);

  // Grid management state
  const getSavedDensity = () => {
    const saved = localStorage.getItem("INTERPRETATIONS_GRID_DENSITY");
    return saved || "standard";
  };

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("INTERPRETATIONS_COLUMNS_VISIBLE");
    const parsed = saved ? JSON.parse(saved) : {};

    // Keep core cost columns visible to avoid stale hidden preferences
    // masking newly available backend fields.
    return {
      ...parsed,
      LinguistPay: true,
      TotalLinguistPay: true,
      AdditionalCost: true,
      MileageCost: true,
      ParkingFeesTolls: true,
      Address: true,
    };
  };

  const [density, setDensity] = useState(getSavedDensity);
  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);
  const apiRef = useGridApiRef();

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("INTERPRETATIONS_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "INTERPRETATIONS_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  const { setOverlayData, setOverlayName } =
    useStateContext();
  const { refreshPage, showSnackbar } = useStateContext();

  // Set up entity tracking for InterpretationsList to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to "InterpretationsList:all" so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "InterpretationsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const [minMaxDates, setMinMaxDates] = useState();

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get saved months from localStorage or use current month as default
  const getSavedMonths = () => {
    const savedMonths = localStorage.getItem(CONST.INTERPRETATION_MONTHS_KEY);
    return savedMonths ? JSON.parse(savedMonths) : [dayjs().format("MMM")];
  };

  const [selectedMonths, setSelectedMonths] = useState(getSavedMonths);
  const [tempSelectedMonths, setTempSelectedMonths] = useState(getSavedMonths);

  // Year selector data
  const currentYear = dayjs().year();

  // Get saved year from localStorage or use current year as default
  const getSavedYear = () => {
    const savedYear = localStorage.getItem(CONST.INTERPRETATION_YEAR_KEY);
    return savedYear ? JSON.parse(savedYear) : [String(currentYear)];
  };

  // Initialize with current year, will be updated when minMaxDates is available
  const [yearOptions, setYearOptions] = useState([String(currentYear)]);

  const [selectedYear, setSelectedYear] = useState(getSavedYear);
  const [tempSelectedYear, setTempSelectedYear] = useState(getSavedYear);

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfClientsOPIMinimums, setListOfClientsOPIMinimums] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  // 2026-10-08 — Mileage rate table for serviceValidationChecks (Checks
  // column + mini profile side panel); same endpoint the New/Edit overlay
  // reads. Fetch failure degrades to an empty array (rate checks skip).
  const [mileageRates, setMileageRates] = useState([]);
  // 2026-10-09 — Settings-managed check config (GET /validationChecks):
  // enable/disable + severity overrides per check code. Loaded once per
  // screen; fetch failure degrades to null → engine defaults.
  const [checkConfig, setCheckConfig] = useState(null);
  const [isDataListsReady, setIsDataListsReady] = useState(false);
  const [pendingCreateNew, setPendingCreateNew] = useState(false);
  const [pendingPreAssign, setPendingPreAssign] = useState(false);
  const fetchInterpretationsDataRef = useRef(() => {});
  const getDataListsRef = useRef(() => {});
  const hasInitializedDataRef = useRef(false);
  // Scroll position to restore after a refetch (saves bounce the list to top).
  // The DataGrid's virtual scroller is the ONLY scroller on this page (the
  // page itself never scrolls), so we save its pixel offset plus the first
  // visible ROW id — the restore anchors to that row via the grid API, which
  // re-syncs the virtualizer (a raw scrollTop write can leave the scrollbar
  // mid-track while the grid renders from index 0).
  const listScrollRestoreRef = useRef(0);
  const listFirstVisibleRowIdRef = useRef(null);

  // Generate year options when minMaxDates changes
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;

      // Create an array of years between startYear and endYear (inclusive)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }

      setYearOptions(years);

      // If the current selected year is not in the range, update it to the current year
      // or the closest year in the range
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          // Choose the closest year in range to the current year
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }

          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]);

  const [contextMenu, setContextMenu] = useState(null);

  const [listOfServices, setListOfServices] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  // Status submenu state (context-menu "Status >" item)
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const statusMenuCloseTimerRef = useRef(null);

  // Derived selection for context menu actions
  const selectedInterpretation = useMemo(() => {
    return (listOfServices || []).find(({ id }) => id === selectedRow);
  }, [listOfServices, selectedRow]);

  // 2026-10-08 — Mini profile side panel (desktop single-click). The row is
  // derived live from listOfServices so socket refetches update the panel in
  // place; a row that leaves the list closes the panel
  // ({sidePanelRow && sidePanelValues && ...}).
  const [sidePanelRowId, setSidePanelRowId] = useState(null);

  const sidePanelRow = useMemo(
    () =>
      (listOfServices || []).find((row) => row.id === sidePanelRowId) || null,
    [listOfServices, sidePanelRowId],
  );

  // 2026-10-12 (Amine) — side-panel footer "more actions" (⋮) menu anchor.
  // Mirrors the row context menu for the panel's row; dismissed when the
  // panel closes or swaps rows.
  const [panelMenuAnchor, setPanelMenuAnchor] = useState(null);

  // 2026-10-08 (Amine) — resizable docked panel. The width lives on the
  // panel only: during a drag the handle updates the panel's DOM width
  // directly (no grid re-render) and commits a single state update on
  // release; the final width persists across reloads.
  // 2026-10-08 (Amine): desktop-only render + breakpoint-responsive default
  // width; manual drag persists as an override; double-click resets.
  // `null` = no manual override → the panel follows the breakpoint default.
  const [sidePanelWidth, setSidePanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem(SIDE_PANEL_WIDTH_STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0
      ? clampSidePanelWidth(saved)
      : null;
  });
  const [isSidePanelResizing, setIsSidePanelResizing] = useState(false);
  const sidePanelGridRef = useRef(null);
  const sidePanelRef = useRef(null);

  // 2026-10-08 (Amine) — breakpoint-responsive default width: bigger
  // screens get a bigger panel. Only used while no manual override exists.
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isXlUp = useMediaQuery(theme.breakpoints.up("xl"));
  const responsiveSidePanelWidth = isXlUp ? 560 : isLgUp ? 480 : isMdUp ? 420 : 380;

  // Viewport mirror — keeps the container cap fresh for stored/dragged
  // overrides on small windows (window resize → re-render → fresh cap).
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleViewportResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleViewportResize);
    return () => window.removeEventListener("resize", handleViewportResize);
  }, []);

  // Cap: never let the panel swallow the whole grid — keep at least ~120px
  // of DataGrid visible, hard-capped at the max width.
  const sidePanelMaxWidth = Math.max(
    SIDE_PANEL_MIN_WIDTH,
    Math.min(
      SIDE_PANEL_MAX_WIDTH,
      (sidePanelGridRef.current?.clientWidth || viewportWidth) - 120,
    ),
  );
  // The width actually rendered: manual override wins, else the breakpoint
  // default; always clamped into [min, max].
  const effectiveSidePanelWidth = Math.max(
    SIDE_PANEL_MIN_WIDTH,
    Math.min(sidePanelWidth ?? responsiveSidePanelWidth, sidePanelMaxWidth),
  );

  const handleSidePanelResizeStart = (event) => {
    event.preventDefault();
    if (event.currentTarget && event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is best-effort; the window listeners below are the
        // source of truth for move/up.
      }
    }

    const startX = event.clientX;
    // Drag starts from the width currently on screen: the manual override
    // when present, otherwise the breakpoint-responsive default (null-safe —
    // `effectiveSidePanelWidth` is always a number).
    const startWidth = effectiveSidePanelWidth;
    const widthFor = (clientX) => {
      // Fresh cap per call — the grid/window can change size mid-drag.
      const maxAllowed = Math.max(
        SIDE_PANEL_MIN_WIDTH,
        Math.min(
          SIDE_PANEL_MAX_WIDTH,
          (sidePanelGridRef.current?.clientWidth || viewportWidth) - 120,
        ),
      );
      return Math.round(
        Math.min(
          maxAllowed,
          Math.max(SIDE_PANEL_MIN_WIDTH, startWidth + (startX - clientX)),
        ),
      );
    };
    let lastClientX = startX;

    const handlePointerMove = (moveEvent) => {
      lastClientX = moveEvent.clientX;
      if (sidePanelRef.current) {
        sidePanelRef.current.style.width = `${widthFor(moveEvent.clientX)}px`;
      }
    };
    const handlePointerEnd = () => {
      const finalWidth = widthFor(lastClientX);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (sidePanelRef.current) {
        sidePanelRef.current.style.width = "";
      }
      localStorage.setItem(SIDE_PANEL_WIDTH_STORAGE_KEY, String(finalWidth));
      setIsSidePanelResizing(false);
      setSidePanelWidth(finalWidth);
    };

    setIsSidePanelResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerEnd);
    window.addEventListener("pointercancel", handlePointerEnd);
  };

  // 2026-10-08 — id → linguist lookup for serviceValidationChecks (Checks
  // column + side panel), built from the same brief list the New/Edit
  // overlay receives.
  const linguistsById = useMemo(() => {
    const map = {};
    (listOfLinguists || []).forEach((linguist) => {
      if (linguist && linguist.id !== undefined) {
        map[linguist.id] = linguist;
      }
    });
    return map;
  }, [listOfLinguists]);

  // 2026-10-13 (Amine) — the side-panel footer ⋮ menu mirrors the Assignment
  // Profile's right-column Actions panel. Its gates recompute from the
  // selected LIST row (the same row the panel shows — the desktop grid click
  // keeps selectedRow and sidePanelRowId in lockstep); the grid's right-click
  // context menu keeps using canCompleteSelected below.
  const panelMenuRow = useMemo(
    () => (listOfServices || []).find(({ id }) => id === selectedRow) || null,
    [listOfServices, selectedRow],
  );

  // Profile gate (InterpretationProfile "Complete Assignment"): the item
  // hides when the status includes "Completed" or "Cancelled".
  const canCompletePanelSelected = Boolean(
    panelMenuRow &&
      !(
        panelMenuRow.InterpretationStatus &&
        (panelMenuRow.InterpretationStatus.includes("Completed") ||
          panelMenuRow.InterpretationStatus.includes("Cancelled"))
      ),
  );

  // Profile gates (`canMarkAsPaid` / `canMarkAsUnpaid`): paid = not paid AND
  // not invoiced; unpaid = paid AND no linked invoice. NOTE: the
  // interpretations list payload does not include Invoice / InvoiceID
  // (whitelisted attributes in server/routes/services/Interpretations.js),
  // so the linked-invoice check evaluates false on list rows and Mark As
  // Unpaid follows Paid alone — the profile's stricter guard applies to its
  // full-record fetch.
  const hasLinkedInvoicePanelSelected = Boolean(
    panelMenuRow?.Invoice?.id || panelMenuRow?.InvoiceID,
  );
  const canMarkPanelSelectedAsPaid = Boolean(
    panelMenuRow && !panelMenuRow.Paid && !panelMenuRow.Invoiced,
  );
  const canMarkPanelSelectedAsUnpaid = Boolean(
    panelMenuRow?.Paid && !hasLinkedInvoicePanelSelected,
  );

  const canCompleteSelected =
    selectedInterpretation &&
    (selectedInterpretation.InterpretationStatus === "Confirmed" ||
      selectedInterpretation.InterpretationStatus === "Requested");

  // Statuses offered in the context-menu submenu (Cancelled variants excluded)
  const statusOptions = ["Requested", "Confirmed", "Completed"];
  const currentStatus = selectedInterpretation?.InterpretationStatus;

  // Selection controls
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  // Round-7 tap-bulletproofing (Amine, 4th on-device report): the long-press
  // ghost-click gate is a TIME WINDOW, not a boolean. A missed clear path on
  // a boolean swallowed the next tap forever; a timestamp expires
  // structurally shortly after the long-press that opened it.
  const suppressClickUntilRef = useRef(0);
  // Stamps the last touchend so the row handlers can tell the synthetic
  // mouse events that follow a touch (mousedown → mouseup → click) apart
  // from a genuine new press.
  const suppressTouchGraceRef = useRef(0);
  const LONG_PRESS_MS = 500;
  // Round-5 tap fix (Amine: "I tapped an item card, and it didn't open"):
  // a long-press suppress guard whose ghost click never arrived (Android
  // contextmenu, drag-off, pointercancel) must never swallow the NEXT tap.
  // A mousedown older than this since the last touchend is a real press.
  const GHOST_MOUSE_MS = 1000;
  // Round-7: window for the ghost click that follows a long-press — it
  // lands within a few frames of the release; 700ms covers slow devices
  // and can never block a later, genuine tap.
  const GHOST_CLICK_WINDOW_MS = 700;

  // 2026-10-08 — Long-press arms bulk selection; the mini profile panel
  // targets one row and closes as soon as selection arms (same dismissal
  // semantics as the context menu).
  useEffect(() => {
    if (selectionEnabled) {
      setSidePanelRowId(null);
    }
  }, [selectionEnabled]);

  // 2026-10-08 (Amine) — the side panel is desktop-only. Crossing into a
  // mobile viewport (shrinking the window below 600px) drops the open row
  // so hidden-panel state can't linger; the render condition is the hard
  // guard, this just keeps state clean.
  useEffect(() => {
    if (isMobile && sidePanelRowId != null) {
      setSidePanelRowId(null);
    }
  }, [isMobile, sidePanelRowId]);

  // 2026-10-12 (Amine) — the footer ⋮ menu belongs to the panel's row:
  // closing the panel or swapping to another row dismisses it.
  useEffect(() => {
    setPanelMenuAnchor(null);
  }, [sidePanelRowId]);

  // Calendar sync progress overlay
  const [calendarSyncProgress, setCalendarSyncProgress] = useState({
    isOpen: false,
    current: 0,
    total: 0,
  });

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Client" -> "INTERPRETATIONS_FILTER_CLIENT"
    const upperFieldName = fieldName.toUpperCase();
    return `INTERPRETATIONS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      // Remove from localStorage if no values selected
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    // Get all column field names from the columns array
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "TimeStart",
      "TimeEnd",
      "Duration",
      "Details",
      "InterpretationStatus",
      "AccountCharge",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Linguist",
      "TargetLanguage",
      "Profit",
      "Margin",
      "InterpretationType",
      "Address",
      "MileageRoundtrip",
      "MileageCost",
      "ParkingFeesTolls",
      "PreparationTime",
      "SchedulingTime",
      "ActualStart",
      "ActualEnd",
      "TotalDuration",
      "Rate",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
      "Checks",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "TimeStart",
      "TimeEnd",
      "Duration",
      "Details",
      "InterpretationStatus",
      "AccountCharge",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Linguist",
      "TargetLanguage",
      "Profit",
      "Margin",
      "InterpretationType",
      "Address",
      "MileageRoundtrip",
      "MileageCost",
      "ParkingFeesTolls",
      "PreparationTime",
      "SchedulingTime",
      "ActualStart",
      "ActualEnd",
      "TotalDuration",
      "Rate",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
      "Checks",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const handleContextMenu = (event) => {
    event.preventDefault();

    const rowId = Number(event.currentTarget.getAttribute("data-id"));
    setSelectedRow(rowId);

    if (Number.isFinite(rowId)) {
      setSelectionModel((prev) => {
        if (selectionEnabled) {
          return prev.includes(rowId) ? prev : [...prev, rowId];
        }
        return [rowId];
      });
    }

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        const interpretationData = response.data;

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_EDIT,
          Interpretations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
        };

        setOverlayData(secondData);

        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  // Pre-Assign Outreach from the list context menu (2026-10-01, per Amine):
  // fetch the assignment (same call the Edit action uses) and open the SAME
  // outreach overlay the assignment profile opens, with the list's linguist
  // cache. Works for any row — the overlay handles the already-assigned case.
  const handleContextMenuPreAssignOutreach = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        const interpretationData = response.data;

        setOverlayData({
          Assignment: interpretationData,
          ServiceID: interpretationData.id,
          Linguists: listOfLinguists,
        });
        setOverlayName(OVERLAY_NAMES.INTERPRETATION_OUTREACH);
        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Failed to open Pre-Assign Outreach.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuAddTask = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow,
        ServiceType: "Interpretation",
      },
      Service: {
        id: interpretationData.id,
        ProjectNumber: interpretationData.ProjectNumber,
        Details: interpretationData.Details,
        ServiceCategory: interpretationData.ServiceCategory,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuAddNote = () => {
    const data = {
      ServiceID: selectedRow,
      MemberID: currentUser.id,
      ModelType: "Interpretation",
      Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
    };

    setOverlayData(data);

    setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);

    setContextMenu(null);
  };

  const handleContextMenuComplete = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        const interpretationData = response.data;

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE,
          Interpretations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
          DeliveryTypes: deliveryTypes,
          PaymentTypes: paymentTypes,
        };

        setOverlayData(secondData);

        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuCallRequestor = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    const requestorPhones = interpretationData?.Requestor?.PhoneNumbers || [];
    const requestorPrimary = requestorPhones.find((p) => p.IsPrimary) || requestorPhones[0];
    const requestorPhone = requestorPrimary?.Number || interpretationData?.Requestor?.PhoneNumber;
    if (requestorPhone) {
      window.location.href = `tel:${requestorPhone.replace(/[^0-9+]/g, "")}`;
    }
    setContextMenu(null);
  };

  const handleContextMenuEmailRequestor = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    if (interpretationData?.Requestor?.Email) {
      const email = interpretationData.Requestor.Email;
      const subject =
        "Regarding Assignment N° " + interpretationData.ProjectNumber;
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
        subject,
      )}`;
      window.open(mailtoLink, "_blank");
    }
    setContextMenu(null);
  };

  // Helper to extract cost lines from service for duplication
  // Ensures duplicated services get proper cost-line rows, not just legacy fields
  const extractCostLinesFromService = (serviceData) => {
    if (!Array.isArray(serviceData?.CostLines) || serviceData.CostLines.length === 0) {
      return [];
    }

    return serviceData.CostLines.map((line) => ({
      CostTypeName: line.CostType?.Name || line.CostTypeName,
      SourceLegacyField: line.SourceLegacyField || null,
      Amount: Number(line.Amount || 0).toFixed(2),
      IncludeInTotalCost: line.IncludeInTotalCost ?? true,
      IncludeInAccountCharge: line.IncludeInAccountCharge ?? false,
      DisplayOrder: line.DisplayOrder || 0,
    }));
  };

  const handleContextMenuDuplicateConfirm = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then(async (response) => {
        const interpretationData = response.data;

        if (!interpretationData) return;

        // 2026-10-20 (Amine): the duplicate uses the SAME rule as New for
        // Interpretations — count how many interpretations exist on the
        // selected day, then +1. Read the count from the server for that
        // date so a filtered/stale list can't skew it.
        let newProjectNumber = interpretationData.ProjectNumber;
        try {
          const dateCountResponse = await api.get(
            "/services/interpretations/date-count",
            { params: { date: interpretationData.Date } },
          );
          const dateCount = Number(dateCountResponse.data?.count || 0);
          const index = dateCount + 1;
          const paddedIndex = index < 10 ? `0${index}` : `${index}`;
          newProjectNumber =
            dayjs(interpretationData.Date).format("YYMMDD") + paddedIndex;
        } catch (error) {
          console.error(
            "Error resolving the project number for duplicate:",
            error,
          );
          newProjectNumber = getProjectNumber(
            interpretationData.Date,
            listOfServices,
          );
        }

        // Extract cost lines from source to ensure duplicates get proper cost-line rows
        const duplicateCostLines = extractCostLinesFromService(interpretationData);

        const duplicatedLanguage =
          interpretationData.ServiceTargetLanguages &&
          interpretationData.ServiceTargetLanguages.length > 0
            ? interpretationData.ServiceTargetLanguages[0].Language
            : null;

        const duplicatedData = {
          Clients: listOfClients,
          ClientsOPIMinimums: listOfClientsOPIMinimums,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          Interpretations: listOfServices,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
          ProjectNumber: newProjectNumber,
          CostLines: duplicateCostLines,
          PrefilledData: {
            Date: interpretationData.Date,
            ProjectNumber: newProjectNumber,
            TimeStart: interpretationData.TimeStart || "00:00:00",
            TimeEnd: interpretationData.TimeEnd || "00:00:00",
            TotalDuration: interpretationData.TotalDuration || "00:00:00",
            Duration: interpretationData.Duration || "00:00:00",
            ActualDuration: interpretationData.ActualDuration || "00:00:00",
            Name: interpretationData.Name,
            Details: interpretationData.Details,
            InterpretationStatus: interpretationData.InterpretationStatus,
            AccountCharge: interpretationData.AccountCharge,
            TotalCost: interpretationData.TotalCost,
            AdditionalCost: interpretationData.AdditionalCost,
            AdditionalLinguistCost: interpretationData.AdditionalLinguistCost,
            LinguistPay: interpretationData.LinguistPay,
            Profit: interpretationData.Profit,
            Margin: interpretationData.Margin,
            InterpretationType: interpretationData.ServiceSubtype?.Name || "",
            Mode: interpretationData.Mode,
            Address: interpretationData.Address,
            MileageRoundtrip: interpretationData.MileageRoundtrip,
            MileageCost: interpretationData.MileageCost,
            ParkingFeesTolls: interpretationData.ParkingFeesTolls,
            PaymentFees: interpretationData.PaymentFees,
            EquipmentCost: interpretationData.EquipmentCost,
            TravelTimeCost: interpretationData.TravelTimeCost,
            TravelTimeDuration: interpretationData.TravelTimeDuration,
            PreparationTime: interpretationData.PreparationTime,
            SchedulingTime: interpretationData.SchedulingTime,
            ActualStart: interpretationData.ActualStart,
            ActualEnd: interpretationData.ActualEnd,
            Rate: interpretationData.Rate,
            RateSimultaneous: interpretationData.RateSimultaneous,
            TotalLinguistPay: interpretationData.TotalLinguistPay,
            ClientRateDiscount: interpretationData.ClientRateDiscount,
            DisableAutoChargeCalc: interpretationData.DisableAutoChargeCalc,
            IsShortAssignment: interpretationData.IsShortAssignment,
            ContactPersonName: interpretationData.ContactPersonName,
            ContactPersonNumber: interpretationData.ContactPersonNumber,
            Client: interpretationData.Client,
            Requestor: interpretationData.Requestor,
            Invoicer: interpretationData.Invoicer,
            Linguist: interpretationData.Linguist,
            Language: duplicatedLanguage,
            CostLines: duplicateCostLines,
          },
        };

        setOverlayData(duplicatedData);
        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
        setContextMenu(null);
        closeDialog();
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuDuplicate = () => {
    openDialog({
      theme: theme,
      title: "Duplicate Assignment",
      message:
        "Are you sure you want to duplicate this interpretation assignment?",
      secondaryAction: "Close",
      primaryAction: "Duplicate",
      secondaryOnClick: () => {
        closeDialog();
        setContextMenu(null);
      },
      primaryOnClick: () => {
        handleContextMenuDuplicateConfirm();
      },
    });
  };

  // ── 2026-10-13 (Amine) — side-panel footer ⋮ menu actions ────────────────
  // Faithful list-scope mirrors of the Assignment Profile's right-column
  // Actions panel: same labels, dialogs and payloads, finished with a list
  // refetch instead of the profile's full page refresh / navigate-back.
  // Gates (canCompletePanelSelected / canMarkPanelSelectedAsPaid /
  // canMarkPanelSelectedAsUnpaid) live beside canCompleteSelected above; the
  // grid's right-click context menu keeps its own handlers untouched.

  const handlePanelMarkAsChecked = () => {
    openDialog({
      theme: theme,
      title: "Mark As Checked",
      message:
        "This assignment will be marked as Checked by you on today's date",
      secondaryAction: "Close",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/services/markServiceChecked", {
            data: {
              id: selectedRow,
              CheckedBy: currentUser.id,
              CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            },
          });
          showSnackbar("Assignment marked as Checked", theme, "success");
          fetchInterpretationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark the assignment as Checked.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handlePanelMarkAsPaid = () => {
    openDialog({
      theme: theme,
      title: "Mark As Paid",
      message:
        panelMenuRow?.Client?.Account !== "Private Persons"
          ? "This assignment will be marked as Paid, and won't be invoiced, are you sure you want to continue?"
          : "This assignment will be marked as Paid",
      secondaryAction: "Close",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/services/markServicePaid", {
            data: {
              id: selectedRow,
              Paid: true,
              Invoiced: true,
            },
          });
          showSnackbar(
            "Interpretation Assignment Marked as Paid",
            theme,
            "success",
          );
          fetchInterpretationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark the assignment as Paid.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handlePanelMarkAsUnpaid = () => {
    openDialog({
      theme: theme,
      title: "Mark As Unpaid",
      message:
        "This assignment will be marked as Unpaid and returned to not invoiced status, are you sure you want to continue?",
      secondaryAction: "Close",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/services/markServicePaid", {
            data: {
              id: selectedRow,
              Paid: false,
              Invoiced: false,
              InvoiceID: null,
            },
          });
          showSnackbar("Assignment Marked as Unpaid", theme, "success");
          fetchInterpretationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark the assignment as Unpaid.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handlePanelChangeCategory = () => {
    setOverlayData({
      ServiceID: selectedRow,
      Category: panelMenuRow?.ServiceCategory,
    });
    setOverlayName(OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY);
  };

  const handlePanelDeleteAssignment = () => {
    openDialog({
      theme: theme,
      title: "Delete Assignment",
      message: "Do you really wish to delete this assignment?",
      secondaryAction: "Close",
      primaryAction: "Delete",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/services/deleteService", {
            data: {
              Old: {
                Assignment: {
                  id: selectedRow,
                  Deleted: 0,
                },
              },
              New: {
                Assignment: {
                  id: selectedRow,
                  Deleted: 1,
                },
              },
            },
          });

          // List-scope finish (the profile navigates back instead): close
          // the panel, clear the selection, refetch, confirm.
          setSidePanelRowId(null);
          setSelectedRow(null);
          setSelectionModel([]);
          showSnackbar(
            "Interpretation Assignment Deleted Successfully",
            theme,
            "success",
          );
          fetchInterpretationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to delete the assignment.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  // Communications — the profile passes its full record as
  // `{ Assignment }`; the list row carries every field the overlay reads
  // (Client/Requestor/Linguist/ServiceSubtype/ServiceTargetLanguages,
  // Mode/Name/Details/Address/ProjectNumber), so it is passed as-is.
  const handlePanelSendDetails = () => {
    if (!panelMenuRow) return;
    setOverlayData({ Assignment: panelMenuRow });
    setOverlayName(OVERLAY_NAMES.INTERPRETATION_SEND_DETAILS);
  };

  const handlePanelCheckMileage = () => {
    const origin = panelMenuComms?.linguistAddress;
    const destination = panelMenuComms?.serviceAddress;
    if (!origin || !destination) return;

    const googleMapsLink = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
      origin,
    )}&destination=${encodeURIComponent(destination)}`;

    window.open(googleMapsLink, "_blank");
  };

  // Mailto mirror of the profile's Linguist / Requestor Email items
  // (subject "Regarding Assignment N° {ProjectNumber}", built verbatim).
  const handlePanelEmail = (email) => {
    if (!email) return;
    const subject =
      "Regarding Assignment N° " + (panelMenuRow?.ProjectNumber ?? "");
    window.open(
      `mailto:${email}?subject=${encodeURIComponent(subject)}`,
      "_blank",
    );
  };

  // Same dialable strip as buildDirectContactActions (the panel quick
  // actions) so the link stays dialable whatever the display formatting.
  const handlePanelCall = (phone) => {
    const dialable =
      phone && phone !== "N/A" ? String(phone).replace(/[^\d+]/g, "") : "";
    if (!dialable) return;
    window.location.href = `tel:${dialable}`;
  };

  // ── Utilities — Copy Tracker Row ─────────────────────────────────────────
  // The profile builds the row from its full record; list rows lack
  // ServiceNotes, so the full record is fetched first (the same
  // /services/interpretations/:id the Edit and Complete flows use), then the
  // profile's buildTrackerRow logic runs on it (parameterized copy).
  const formatTrackerDate = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("M/D/YYYY") : "";
  };

  const sanitizeCell = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    return /["\t\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const showManualCopyDialog = (trackerRow) => {
    openDialog({
      theme,
      title: "Clipboard Blocked",
      message: "", // Render instruction in custom content for consistency
      customContent: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2">
            Please click the content below and manually copy it, then paste it
            into the Tracker.
          </Typography>
          <Box
            component="textarea"
            value={trackerRow}
            readOnly
            sx={{
              width: "100%",
              minHeight: 160,
              p: 1,
              fontFamily: "monospace",
              fontSize: 14,
              borderRadius: "8px",
              borderColor: theme.palette.divider,
              bgcolor: theme.palette.background.default,
            }}
            onFocus={(e) => e.target.select()}
          />
        </Box>
      ),
      tertiaryAction: null,
      secondaryAction: "Close",
      primaryAction: null,
      tertiaryOnClick: null,
      secondaryOnClick: () => closeDialog(),
      primaryOnClick: null,
    });
  };

  const buildPanelTrackerRow = (data) => {
    const notesText = (data.ServiceNotes || [])
      .map((note) => note?.Note)
      .filter(Boolean)
      .join("\n");

    const normalizedMargin =
      data.Margin === null || data.Margin === undefined
        ? ""
        : Number(data.Margin) / 100;

    const projectNumberValue = (() => {
      const num = Number(data.ProjectNumber);
      if (Number.isFinite(num)) return Math.trunc(num);
      return data.ProjectNumber || "";
    })();

    const isCompleted = data.InterpretationStatus === "Completed";

    const targetLanguage =
      data.ServiceTargetLanguages && data.ServiceTargetLanguages.length > 0
        ? data.ServiceTargetLanguages[0]?.Language?.Name
        : "N/A";

    const columns = [
      projectNumberValue,
      "",
      formatTrackerDate(data.Date),
      formatTimeToHHMM12Hr(data.TimeStart),
      formatTimeToHHMM12Hr(data.TimeEnd),
      formatDurationToHHMM(data.Duration),
      data.Client?.Account,
      data.Requestor?.Name,
      data.InterpretationStatus,
      data.AccountCharge,
      data.Details,
      data.Linguist?.Name,
      data.TotalCost,
      data.AdditionalCost,
      data.LinguistPay,
      data.Profit,
      normalizedMargin,
      targetLanguage,
      data.ServiceSubtype?.Name,
      notesText,
      data.Address,
      data.Rate,
      formatDurationToHHMM(data.TotalDuration),
      formatMileage(data.MileageRoundtrip),
      data.MileageCost,
      data.ParkingFeesTolls,
      formatDurationToHHMM(data.PreparationTime),
      formatDurationToHHMM(data.SchedulingTime),
      isCompleted ? formatTimeToHHMM12Hr(data.ActualStart) : "",
      isCompleted ? formatTimeToHHMM12Hr(data.ActualEnd) : "",
      isCompleted ? formatDurationToHHMM(data.ActualDuration) : "",
      "",
      "",
      data.CancellationReason,
    ];

    return columns.map(sanitizeCell).join("\t");
  };

  const handlePanelCopyTrackerRow = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then(async (response) => {
        const trackerRow = buildPanelTrackerRow(response.data);

        // If not secure context (e.g., http or direct IP), clipboard API is
        // blocked.
        if (!window.isSecureContext) {
          showManualCopyDialog(trackerRow);
          return;
        }

        try {
          await navigator.clipboard.writeText(trackerRow);
          showSnackbar("Tracker Row copied successfully.", theme, "success");
        } catch (error) {
          console.error("Error copying tracker row:", error);
          showManualCopyDialog(trackerRow);
        }
      })
      .catch((error) => {
        console.error("Error fetching assignment for tracker row:", error);
        handleErrorDialog("Failed to copy tracker row", theme);
      });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setStatusMenuAnchor(null);
  };

  // Status submenu handlers
  const handleStatusMenuOpen = (event) => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleStatusMenuEnter = () => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
  };

  const handleStatusMenuClose = () => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
    setStatusMenuAnchor(null);
  };

  const handleStatusMenuLeave = () => {
    statusMenuCloseTimerRef.current = setTimeout(() => {
      setStatusMenuAnchor(null);
    }, 150);
  };

  const handleContextMenuStatusChange = (status) => {
    setStatusMenuAnchor(null);
    setContextMenu(null);

    if (status === currentStatus) return;

    api
      .post("/services/updateStatus", {
        serviceId: selectedRow,
        status,
      })
      .then((response) => {
        const updatedService = response.data;
        showSnackbar(
          `Status updated to "${status}" for ${updatedService.ProjectNumber || "service"}.`,
          theme,
          "success",
        );

        // Refresh the list (existing UI invalidation behavior)
        fetchInterpretationsData(selectedMonths, selectedYear);

        // Emit assignment saved events for other viewers (same as the edit flow)
        if (socket) {
          const userPayload = {
            id: currentUser.id,
            name: currentUser.Name,
            imageThumbnail: currentUser.ImageThumbnail,
          };
          socket.emit("assignment:saved", {
            modelType: "Interpretation",
            recordId: updatedService.id,
            user: userPayload,
            projectNumber: updatedService.ProjectNumber,
          });
          socket.emit("assignment:saved", {
            modelType: "InterpretationsList",
            recordId: "all",
            user: userPayload,
            projectNumber: updatedService.ProjectNumber,
          });
        }
      })
      .catch((error) => {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update service status.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    // New-press hygiene: a leftover long-press suppress guard (its ghost
    // click never came) must not swallow this press's click. The synthetic
    // mousedown that follows a touch keeps the guard — its click is still
    // on its way (see suppressTouchGraceRef).
    if (Date.now() - suppressTouchGraceRef.current > GHOST_MOUSE_MS) {
      suppressClickUntilRef.current = 0;
    }
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressClickUntilRef.current = Date.now() + GHOST_CLICK_WINDOW_MS;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    // A new physical touch always starts clean: any earlier ghost click has
    // already fired (or the browser cancels it when a new touch arrives).
    // Without this, a stale guard swallows the very next card tap.
    suppressClickUntilRef.current = 0;
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressClickUntilRef.current = Date.now() + GHOST_CLICK_WINDOW_MS;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
    suppressTouchGraceRef.current = Date.now();
  };

  const handleRowSelectionModelChange = (newSelection) => {
    if (selectionEnabled) {
      setSelectionModel(newSelection);
    } else {
      // keep last selected for highlighting
      setSelectionModel(newSelection.slice(-1));
    }
  };

  const handleCancelSelection = () => {
    setSelectionModel([]);
    setSelectionEnabled(false);
    suppressClickUntilRef.current = 0;
  };

  // Auto-disarm (Amine 2026-10-05, round 6): when the ARMED selection model
  // empties through user toggling — the last selected card tapped OFF —
  // selection mode exits so the NEXT tap opens the card normally instead of
  // silently toggling again. The ref only lets the effect disarm after the
  // armed session has SEEN a non-empty model: the long-press arms
  // `selectionEnabled` and appends its id in the same callback, and the ref
  // guard keeps any transitional render (armed + empty model) from disarming
  // instantly.
  const armedSawSelectionRef = useRef(false);
  useEffect(() => {
    if (!selectionEnabled) {
      armedSawSelectionRef.current = false;
      return;
    }
    if (selectionModel.length > 0) {
      armedSawSelectionRef.current = true;
      return;
    }
    if (armedSawSelectionRef.current) {
      armedSawSelectionRef.current = false;
      setSelectionEnabled(false);
    }
  }, [selectionEnabled, selectionModel]);

  const isServiceChecked = useCallback((service) => {
    return !!(service?.CheckingDate || service?.CheckedBy || service?.Checker);
  }, []);

  const isServiceEligibleForCalendar = useCallback((service) => {
    return (
      service?.ServiceCategory === "Interpretation" &&
      service?.ServiceSubtype?.Name !== "OPI"
    );
  }, []);

  const hasCalendarEvent = useCallback((service) => {
    return !!service?.graphEventId;
  }, []);

  const selectionStats = useMemo(() => {
    if (!selectionEnabled || selectionModel.length === 0) {
      return { total: 0, checked: 0, unchecked: 0, allChecked: false };
    }

    const selectedSet = new Set(selectionModel);
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedSet.has(service.id),
    );

    const checked = selectedServices.filter(isServiceChecked).length;
    const total = selectedServices.length;
    const unchecked = Math.max(total - checked, 0);

    return {
      total,
      checked,
      unchecked,
      allChecked: total > 0 && unchecked === 0,
    };
  }, [selectionEnabled, selectionModel, listOfServices, isServiceChecked]);

  const handleMarkSelectedAsChecked = () => {
    if (selectionModel.length === 0) return;

    const selectedIds = [...selectionModel];
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedIds.includes(service.id),
    );
    const uncheckedIds = selectedServices
      .filter((service) => !isServiceChecked(service))
      .map((service) => service.id);

    if (uncheckedIds.length === 0) {
      showSnackbar(
        "The selected assignment(s) are already checked.",
        theme,
        "info",
      );
      return;
    }

    const message =
      uncheckedIds.length === selectedIds.length
        ? `Mark ${selectedIds.length} assignment(s) as Checked?`
        : `Only ${uncheckedIds.length} out of ${selectedIds.length} of the selected assignments are not checked, do you want to mark them as checked?`;

    openDialog({
      theme,
      title: "Mark Assignments as Checked",
      message,
      secondaryAction: "Cancel",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          const payloads = uncheckedIds.map((id) => {
            const data = {
              id,
              CheckedBy: currentUser.id,
              CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            };
            return api.post("/services/markServiceChecked", {
              data,
            });
          });

          await Promise.all(payloads);
          showSnackbar("Assignments marked as Checked", theme, "success");
          handleCancelSelection();
          fetchInterpretationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark interpretations as checked.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handleAddSelectedToCalendar = () => {
    if (selectionModel.length === 0) return;

    const selectedIds = [...selectionModel];
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedIds.includes(service.id),
    );

    // Filter for eligible services without calendar events
    const eligibleIds = selectedServices
      .filter(
        (service) =>
          isServiceEligibleForCalendar(service) && !hasCalendarEvent(service),
      )
      .map((service) => service.id);

    if (eligibleIds.length === 0) {
      const ineligibleCount = selectedServices.filter(
        (s) => !isServiceEligibleForCalendar(s),
      ).length;
      const alreadyHaveEvents = selectedServices.filter(
        (s) => isServiceEligibleForCalendar(s) && hasCalendarEvent(s),
      ).length;

      let reason = "";
      if (ineligibleCount > 0 && alreadyHaveEvents > 0) {
        reason = `${ineligibleCount} are not interpretations and ${alreadyHaveEvents} already have calendar events.`;
      } else if (ineligibleCount > 0) {
        reason = `${ineligibleCount} of the selected assignments are not eligible interpretations.`;
      } else if (alreadyHaveEvents > 0) {
        reason = `All ${alreadyHaveEvents} selected assignments already have calendar events.`;
      }

      showSnackbar(`No eligible assignments to add. ${reason}`, theme, "info");
      return;
    }

    const message =
      eligibleIds.length === selectedIds.length
        ? `Add ${eligibleIds.length} assignment(s) to calendar?`
        : `Only ${eligibleIds.length} out of ${selectedIds.length} selected assignments are eligible (must be Interpretations without calendar events). Add these to calendar?`;

    openDialog({
      theme,
      title: "Add Assignments to Calendar",
      message,
      secondaryAction: "Cancel",
      primaryAction: "Add to Calendar",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          setCalendarSyncProgress({
            isOpen: true,
            current: 0,
            total: eligibleIds.length,
          });
          closeDialog();

          let successCount = 0;
          let failureCount = 0;

          // Process each service individually to track progress
          for (let i = 0; i < eligibleIds.length; i++) {
            const serviceId = eligibleIds[i];
            try {
              await api.post(`/calendar/sync/${serviceId}`);
              successCount++;
            } catch (error) {
              failureCount++;
            }
            // Update progress
            setCalendarSyncProgress({
              isOpen: true,
              current: i + 1,
              total: eligibleIds.length,
            });
          }

          // Close progress overlay
          setCalendarSyncProgress({ isOpen: false, current: 0, total: 0 });

          // Show result summary
          if (failureCount === 0) {
            showSnackbar(
              `${successCount} assignment(s) added to calendar`,
              theme,
              "success",
            );
          } else {
            showSnackbar(
              `${successCount} added, ${failureCount} failed`,
              theme,
              failureCount > 0 ? "warning" : "success",
            );
          }

          handleCancelSelection();
          fetchInterpretationsData(selectedMonths, selectedYear);
        } catch (error) {
          setCalendarSyncProgress({ isOpen: false, current: 0, total: 0 });
          const message =
            error.response?.data?.message ||
            "Failed to add assignments to calendar.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        }
      },
    });
  };

  // Handle date selection save (combined months and years)
  const handleDateSelectionSave = (newSelection) => {
    // Ensure only one year is selected
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Take only the last selected year
    };

    // Update state
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);

    // Save to localStorage
    localStorage.setItem(
      CONST.INTERPRETATION_MONTHS_KEY,
      JSON.stringify(updatedSelection.months),
    );
    localStorage.setItem(
      CONST.INTERPRETATION_YEAR_KEY,
      JSON.stringify(updatedSelection.years),
    );

    // Fetch data
    fetchInterpretationsData(updatedSelection.months, updatedSelection.years);
  };

  const fetchInterpretationsData = useCallback(
    (months = selectedMonths, year = selectedYear) => {
      setIsLoading(true);

      // Capture the current scroll position so a refetch (e.g. after saving
      // an edit) doesn't bounce the list back to the top. Save the virtual
      // scroller's pixel offset AND the first VISIBLE row id (anchoring to a
      // row is the reliable way to resync the virtualizer on restore).
      // Queries are scoped to THIS grid's root element via its own apiRef —
      // a global document.querySelector can match a DIFFERENT grid mounted
      // elsewhere on the page (that is why the restore worked on one list
      // but not another).
      const captureRoot = apiRef.current?.rootElementRef?.current || document;
      const scroller = captureRoot.querySelector(
        ".MuiDataGrid-virtualScroller",
      );
      if (scroller && scroller.scrollTop > 0) {
        listScrollRestoreRef.current = scroller.scrollTop;
      }
      // Only anchor by row when the user was actually scrolled down — at the
      // top we must stay at the top (a new row inserted above would otherwise
      // yank the list). Pick the first row actually VISIBLE in the viewport
      // (the first DOM row may be an off-screen buffer row above it).
      let firstVisibleRowEl = null;
      if (scroller && scroller.scrollTop > 0) {
        const scRect = scroller.getBoundingClientRect();
        const rowEls = scroller.querySelectorAll(".MuiDataGrid-row[data-id]");
        for (const el of rowEls) {
          if (el.getBoundingClientRect().bottom > scRect.top + 1) {
            firstVisibleRowEl = el;
            break;
          }
        }
      }
      listFirstVisibleRowIdRef.current = firstVisibleRowEl
        ? firstVisibleRowEl.getAttribute("data-id")
        : null;
      // Ensure we have valid parameters
      let finalMonths = months;
      let finalYear = year;

      if (!finalMonths || finalMonths.length === 0) {
        finalMonths = [dayjs().format("MMM")];
        // Update both states to reflect the default value
        setSelectedMonths(finalMonths);
        setTempSelectedMonths(finalMonths);
      }

      if (!finalYear || finalYear.length === 0) {
        finalYear = [String(currentYear)];
        // Update both states to reflect the default value
        setSelectedYear(finalYear);
        setTempSelectedYear(finalYear);
      }

      // Convert months to a comma-separated string if it's an array
      const monthsParam = Array.isArray(finalMonths)
        ? finalMonths.join(",")
        : finalMonths;

      api
        .get("/services/interpretations", {
          params: {
            months: monthsParam,
            year: finalYear[0], // Since year is single-select, we only need the first (and only) value
          },
        })
        .then((response) => {
          if (response !== null) {
            //REVERSE LISTS
            const services = response.data;
            services.reverse();
            setListOfServices(services);
            setIsLoading(false);

            // Restore the scroll position once the new rows are rendered.
            // Phase 1: materialize the anchor row (single scrollToIndexes).
            // Phase 2: nudge by the measured rect delta with a settle frame
            // between every write and the next measurement — measuring on the
            // same frame as the write reads stale layout and overshoots,
            // which caused the up/down jitter. Stops as soon as the anchor is
            // aligned, when a correction stops reducing the error, or on user
            // interaction. Scoped to THIS grid's root element via its apiRef.
            const savedRowId = listFirstVisibleRowIdRef.current;
            const savedPixel = listScrollRestoreRef.current;
            if (savedRowId != null || savedPixel > 0) {
              const targetIdx =
                savedRowId != null
                  ? services.findIndex(
                      (s) => String(s.id) === String(savedRowId),
                    )
                  : -1;

              const getScroller = () => {
                const root = apiRef.current?.rootElementRef?.current;
                return (root || document).querySelector(
                  ".MuiDataGrid-virtualScroller",
                );
              };
              let cancelled = false;
              const cancelOnInteraction = () => {
                cancelled = true;
              };
              const interactionEvents = ["wheel", "mousedown", "touchstart"];
              const initialScroller = getScroller();
              interactionEvents.forEach((evt) => {
                initialScroller?.addEventListener(evt, cancelOnInteraction, {
                  once: true,
                  passive: true,
                });
              });

              const cleanup = () => {
                listScrollRestoreRef.current = 0;
                listFirstVisibleRowIdRef.current = null;
                const sc = getScroller();
                interactionEvents.forEach((evt) => {
                  sc?.removeEventListener(evt, cancelOnInteraction);
                });
              };

              let materialized = targetIdx < 0;
              let settleFrames = 0;
              let attempts = 0;
              let lastAbsDelta = null;
              const maxAttempts = 40;

              const step = () => {
                if (cancelled) {
                  cleanup();
                  return;
                }

                const sc = getScroller();
                if (!sc) {
                  attempts += 1;
                  if (attempts < maxAttempts) requestAnimationFrame(step);
                  else cleanup();
                  return;
                }

                // Phase 1 — bring the anchor row into the render window once.
                if (!materialized) {
                  apiRef.current?.scrollToIndexes?.({ rowIndex: targetIdx });
                  materialized = true;
                  settleFrames = 1;
                  requestAnimationFrame(step);
                  return;
                }

                // Let the grid apply the last write before measuring again.
                if (settleFrames > 0) {
                  settleFrames -= 1;
                  requestAnimationFrame(step);
                  return;
                }

                if (targetIdx >= 0) {
                  const rowEl = sc.querySelector(
                    `.MuiDataGrid-row[data-id="${savedRowId}"]`,
                  );
                  if (!rowEl) {
                    // Virtualizer still settling — wait (bounded).
                    attempts += 1;
                    if (attempts < maxAttempts) requestAnimationFrame(step);
                    else cleanup();
                    return;
                  }

                  const delta =
                    rowEl.getBoundingClientRect().top -
                    sc.getBoundingClientRect().top;
                  const absDelta = Math.abs(delta);
                  if (absDelta <= 2) {
                    cleanup();
                    return;
                  }
                  // If the previous nudge did not reduce the error, stop —
                  // re-asserting would just jitter between two positions.
                  if (lastAbsDelta !== null && absDelta >= lastAbsDelta - 1) {
                    cleanup();
                    return;
                  }
                  lastAbsDelta = absDelta;
                  sc.scrollTop += delta;
                  settleFrames = 1;
                } else {
                  // No usable row anchor — restore the pixel offset and nudge
                  // the virtualizer with a scroll event (a no-op scrollTop
                  // write fires no event, so the render window never updates).
                  const pxDiff = Math.abs(sc.scrollTop - savedPixel);
                  if (pxDiff <= 2) {
                    cleanup();
                    return;
                  }
                  if (lastAbsDelta !== null && pxDiff >= lastAbsDelta - 1) {
                    cleanup();
                    return;
                  }
                  lastAbsDelta = pxDiff;
                  sc.scrollTop = savedPixel;
                  sc.dispatchEvent(new Event("scroll"));
                  settleFrames = 1;
                }

                attempts += 1;
                if (attempts < maxAttempts && !cancelled) {
                  requestAnimationFrame(step);
                } else {
                  cleanup();
                }
              };
              requestAnimationFrame(step);
            }
          }
        })
        .catch((error) => {
          const message =
            error.response?.data?.message ||
            "Unable to connect to the server. Please try again later.";
          handleErrorDialog(message, theme);

          setIsLoading(false);
        });
    },
    [currentYear, selectedMonths, selectedYear, handleErrorDialog, theme, apiRef],
  );

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/clients/OPIMinimums"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
        api.get("/dashboard/serviceDateRange"),
        api.get("/services/translations/deliveryTypes"),
        api.get("/services/translations/paymentTypes"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            clientsOPIMinimumsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            minMaxDatesResponse,
            deliveryTypesResponse,
            paymentTypesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              const tempLanguages = typesResponse.data.slice(0, 5);
              setListOfTypes(tempLanguages);
            }
            if (clientsOPIMinimumsResponse) {
              setListOfClientsOPIMinimums(clientsOPIMinimumsResponse.data);
            }
            if (minMaxDatesResponse) {
              setMinMaxDates(minMaxDatesResponse.data);
            }
            if (deliveryTypesResponse) {
              const filteredDeliveryTypes = deliveryTypesResponse.data.filter(
                (type) => !["CODA", "SmartCat", "In-person"].includes(type),
              );
              setDeliveryTypes(filteredDeliveryTypes);
            }
            if (paymentTypesResponse) {
              setPaymentTypes(paymentTypesResponse.data);
            }
            // Mark data lists as ready
            setIsDataListsReady(true);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsDataListsReady(true); // Even on error, mark as ready to prevent infinite waiting
      });
  }, [handleErrorDialog, theme]);

  fetchInterpretationsDataRef.current = fetchInterpretationsData;
  getDataListsRef.current = getDataLists;

  useEffect(() => {
    fetchInterpretationsDataRef.current();
    getDataListsRef.current();
    hasInitializedDataRef.current = true;
  }, []);

  // 2026-10-08 — Checks column / side panel: mileage rate table fetched
  // once (silent on failure; checks that need rates skip when empty).
  useEffect(() => {
    api
      .get("/mileageRates")
      .then((response) => {
        setMileageRates(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        setMileageRates([]);
      });
  }, []);

  // 2026-10-09 — Checks column / side panel: Settings check config (module-
  // cached, one fetch per session). Failure → null → engine defaults. The
  // Checks column captures `checkConfig` in its closures, so the state bump
  // re-renders the grid with the same flow as `mileageRates`.
  useEffect(() => {
    let cancelled = false;

    loadValidationCheckConfig().then((config) => {
      if (!cancelled && Array.isArray(config)) {
        setCheckConfig(config);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hasInitializedDataRef.current) {
      return;
    }

    fetchInterpretationsDataRef.current();
  }, [refreshPage]);

  // Listen for interpretation updates from other members
  const handleInterpretationsListRefetch = useCallback(
    (updatedBy) => {
      // Reload the interpretations list data
      fetchInterpretationsData();
    },
    [fetchInterpretationsData],
  );

  // IDs currently visible in the filtered list — updates for records outside
  // this set are ignored (a May change doesn't reload a list filtered to August)
  const visibleServiceIds = useMemo(
    () => (listOfServices || []).map((s) => Number(s.id)),
    [listOfServices],
  );

  useAssignmentUpdates({
    modelType: "InterpretationsList",
    recordId: "all",
    onUpdate: handleInterpretationsListRefetch,
    visibleRecordIds: visibleServiceIds,
  });

  // Handle pending create new action when data lists are ready
  useEffect(() => {
    if (pendingCreateNew && isDataListsReady) {
      const data = {
        Clients: listOfClients,
        ClientsOPIMinimums: listOfClientsOPIMinimums,
        Linguists: listOfLinguists,
        Languages: listOfLanguages,
        Interpretations: listOfServices,
        InterpretationStatuses: listOfStatuses,
        InterpretationTypes: listOfTypes,

        Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
      };

      setOverlayData(data);
      setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
      setPendingCreateNew(false);
    }
  }, [
    pendingCreateNew,
    isDataListsReady,
    listOfClients,
    listOfClientsOPIMinimums,
    listOfLinguists,
    listOfLanguages,
    listOfServices,
    listOfStatuses,
    listOfTypes,
    setOverlayData,
    setOverlayName,
  ]);

  useEffect(() => {
    if (pendingPreAssign && isDataListsReady) {
      const data = {
        Clients: listOfClients,
        ClientsOPIMinimums: listOfClientsOPIMinimums,
        Linguists: listOfLinguists,
        Languages: listOfLanguages,
        Interpretations: listOfServices,
        InterpretationStatuses: listOfStatuses,
        InterpretationTypes: listOfTypes,
      };

      setOverlayData(data);
      setOverlayName(OVERLAY_NAMES.INTERPRETATION_PRE_ASSIGN);
      setPendingPreAssign(false);
    }
  }, [
    pendingPreAssign,
    isDataListsReady,
    listOfClients,
    listOfClientsOPIMinimums,
    listOfLinguists,
    listOfLanguages,
    listOfServices,
    listOfStatuses,
    listOfTypes,
    setOverlayData,
    setOverlayName,
  ]);

  // Sync the temporary state with the actual state when the actual state changes from outside
  useEffect(() => {
    setTempSelectedMonths(selectedMonths);
  }, [selectedMonths]);

  useEffect(() => {
    setTempSelectedYear(selectedYear);
  }, [selectedYear]);

  const timeComparator = (v1, v2) => {
    const format = "h:mm A"; // Matches "hh:MM a" like "1:30 PM"
    const t1 = dayjs(v1, format);
    const t2 = dayjs(v2, format);

    return t1.isBefore(t2) ? -1 : t1.isAfter(t2) ? 1 : 0;
  };

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  // 2026-10-08 — "OK" / "N issue(s)" summary for the Checks column. Since
  // 2026-10-13 it is used ONLY by the two export writers (the column's
  // valueGetter now uses getChecksBucket for filtering): the CSV/XLSX export
  // reads RAW row fields (with static fieldPaths mappings), not valueGetters,
  // so "Checks" gets a special-case branch there just like TargetLanguage.
  const getChecksSummaryValue = (row) => {
    const issues = runInterpretationChecks(row, {
      mileageRates,
      linguistsById,
      checkConfig,
    });
    return issues.length === 0 ? "OK" : `${issues.length} issue(s)`;
  };

  // 2026-10-13 — Filter-facing bucket for the Checks column: reduces the
  // worst severity from runInterpretationChecks to a single display bucket
  // so the standard ColumnFilter can offer Passed / Warnings / Errors
  // (options are data-driven from this valueGetter, like every other
  // column).
  const getChecksBucket = (row) => {
    const worst = getWorstCheckSeverity(
      runInterpretationChecks(row, {
        mileageRates,
        linguistsById,
        checkConfig,
      }),
    );
    return worst === "error"
      ? "Errors"
      : worst === "warning"
        ? "Warnings"
        : "Passed";
  };

  // 2026-10-08 — Payload shared by the mini profile panel footer, the
  // desktop double-click, and the mobile tap-through (previously duplicated
  // inline in both row handlers).
  const buildProfileNavigationState = (row) => ({
    Interpretation: row,
    InterpretationID: row.id,

    Clients: listOfClients,
    Linguists: listOfLinguists,
    Languages: listOfLanguages,
    Interpretations: listOfServices,
    InterpretationStatuses: listOfStatuses,
    InterpretationTypes: listOfTypes,

    ComingFrom: `/interpretations`,
  });

  const columns = [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 110,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      flex: 1.1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      // 2026-10-13 (Amine) — validation Checks column, second in the grid
      // right after the project number. Filterable through the standard
      // ColumnFilter header (same custom filter system as every other
      // column): options are data-driven from getChecksBucket, which buckets
      // the worst severity into Passed / Warnings / Errors. `sortable: false`
      // keeps it out of the mobile sort sheet; exports still special-case
      // getChecksSummaryValue in both writers (CSV + XLSX).
      // 2026-10-13 (Amine) — renders as a filled severity chip
      // (ServiceChecksIndicator variant="chip") matching the Status column.
      // The column is compact (width 110) so the chip wraps its content —
      // the chip hugs its icon instead of stretching across the cell. 8px
      // radius matching the Status chip. Width widened from 90 → 110
      // (2026-10-13, Amine) so the header "Checks" text and the filter
      // icon both render without clipping.
      field: "Checks",
      headerName: "Checks",
      minWidth: 110,
      flex: 0,
      width: 110,
      sortable: false,
      valueGetter: (_, row) => getChecksBucket(row),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
      renderCell: (params) => {
        const issues = runInterpretationChecks(params.row, {
          mileageRates,
          linguistsById,
          checkConfig,
        });
        return (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ServiceChecksIndicator issues={issues} variant="chip" />
          </Box>
        );
      },
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value =
          data && data !== "" ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 100,
      flex: 1.0,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: getDateProximityColor(
                theme,
                params.value,
                params.row.InterpretationStatus,
              ),
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[1] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data && data.Account ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TimeStart",
      sortComparator: timeComparator,
      headerName: "Time Start",
      valueGetter: (_, params) => {
        const data = params.TimeStart;
        if (data === "00:00:00") {
          return "12:00 AM";
        }
        if (data && data !== "") {
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }
        return "N/A";
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TimeEnd",
      sortComparator: timeComparator,
      headerName: "Time End",
      valueGetter: (_, params) => {
        const data = params.TimeEnd;
        if (data === "00:00:00") {
          return "12:00 AM";
        }
        if (data && data !== "") {
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }
        return "N/A";
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Duration",
      headerName: "Duration",
      valueGetter: (_, params) => {
        const data = params.Duration;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 115,
      flex: 1.15,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "InterpretationStatus",
      headerName: "Status",
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <HtmlTooltip
            title={
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {params.value}
              </Typography>
            }
          >
            <Box
              sx={{
                borderRadius: "8px",
                px: "0.5rem",
                backgroundColor: getStatusColor(theme, params.value),
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: theme.palette.background.default,
                }}
              >
                {params.value}
              </Typography>
            </Box>
          </HtmlTooltip>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "InterpretationType",
      headerName: "Interpretation Type",
      valueGetter: (_, params) => {
        const data = params.ServiceSubtype?.Name;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      minWidth: 185,
      flex: 1.85,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Address",
      headerName: "Address",
      valueGetter: (_, params) => {
        const data = params.Address;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 220,
      flex: 2.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TargetLanguage",
      headerName: "Language",
      valueGetter: (_, params) => {
        const languages = params.ServiceTargetLanguages;
        if (!languages || languages.length === 0) return "N/A";
        // Extract the Language.Name from each item and filter out any empty values
        return languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "AccountCharge",
      headerName: "Account Charge",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AccountCharge;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      minWidth: 165,
      flex: 1.65,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalCost",
      headerName: "Total Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AdditionalCost",
      headerName: "Additional Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "TotalLinguistPay",
      headerName: "Total Linguist Pay",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalLinguistPay;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistPay",
      headerName: "Linguist Pay",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.LinguistPay;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AdditionalLinguistCost",
      headerName: "Additional Linguist Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalLinguistCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 220,
      flex: 2.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "EquipmentCost",
      headerName: "Equipment Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.EquipmentCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },


    {
      field: "MileageRoundtrip",
      headerName: "Mileage Roundtrip",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.MileageRoundtrip;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `${data}`;
        }

        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "MileageCost",
      headerName: "Mileage Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.MileageCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceClientRateSimultaneous",
      headerName: "Client Simultaneous Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.ServiceClientRateSimultaneous;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 230,
      flex: 2.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Rate",
      headerName: "Linguist Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Rate;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "RateSimultaneous",
      headerName: "Linguist Simultaneous Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.RateSimultaneous;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 250,
      flex: 2.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Profit",
      headerName: "Profit",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Profit;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 95,
      flex: 0.95,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Margin",
      headerName: "Margin",
      valueGetter: (_, params) => {
        const data = params.Margin;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          const percentage = data * 1;
          if (percentage === 0) {
            value = "0%";
          } else if (percentage === 100) {
            value = "100%";
          } else {
            value = `${percentage.toFixed(2)}%`;
          }
        }

        return value;
      },
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "PreparationTime",
      headerName: "Preparation Time",
      valueGetter: (_, params) => {
        const data = params.PreparationTime;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 175,
      flex: 1.75,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "SchedulingTime",
      headerName: "Scheduling Time",
      valueGetter: (_, params) => {
        const data = params.SchedulingTime;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    
    {
      field: "ActualStart",
      sortComparator: timeComparator,
      headerName: "Actual Start",
      valueGetter: (_, params) => {
        const data = params.ActualStart;
        if (data === "00:00:00") {
          return "12:00 AM";
        }
        if (data && data !== "") {
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }
        return "N/A";
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ActualEnd",
      sortComparator: timeComparator,
      headerName: "Actual End",
      valueGetter: (_, params) => {
        const data = params.ActualEnd;
        if (data === "00:00:00") {
          return "12:00 AM";
        }
        if (data && data !== "") {
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }
        return "N/A";
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalDuration",
      headerName: "Total Duration",
      valueGetter: (_, params) => {
        const data = params.TotalDuration;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Cancelled",
      headerName: "Cancelled",
      valueGetter: (_, params) => {
        const data = params.Cancelled;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Cancelled;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CancellationReason",
      headerName: "Cancellation Reason",
      valueGetter: (_, params) => {
        const data = params.CancellationReason;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      minWidth: 200,
      flex: 2.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Invoiced",
      headerName: "Invoiced",
      valueGetter: (_, params) => {
        const data = params.Invoiced;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Invoiced;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CreatedBy",
      headerName: "Created By",
      valueGetter: (_, params) => {
        const data = params.Creator;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedBy",
      headerName: "Checked By",
      valueGetter: (_, params) => {
        const data = params.Checker;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedOn",
      headerName: "Checked On",
      valueGetter: (_, params) => {
        const data = params.CheckingDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";

        return value;
      },
      sortComparator: dateComparator,
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CONST.INTERPRETATION_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const getStoredSortModel = () => {
    const stored = localStorage.getItem(CONST.INTERPRETATION_SORT_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const [filterModel, setFilterModel] = useState(getStoredFilterModel);
  const [filterRowIds, setFilterRowIds] = useState([]);
  const prevFilteredRowIdsRef = useRef([]);
  const [sortModel, setSortModel] = useState(getStoredSortModel);

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfServices || [], columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);
  // Export dialog bridge — reassigned every commit by the effect placed after
  // the ExportDialogContent definition, so the memoized toolbar's Export
  // MenuItem always opens the LATEST render's dialog instead of a stale
  // captured one (whose data can predate the grid load).
  const openExportDialogRef = useRef(null);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback((field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  }, [handleColumnFilterChange, saveColumnFilter]);

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listOfServices || listOfServices.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listOfServices, loadAllColumnFilters]); // Apply saved filters once per loaded dataset

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
    localStorage.setItem(
      CONST.INTERPRETATION_FILTER_KEY,
      JSON.stringify({ items: [] }),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (row) =>
          row.ProjectNumber && row.ProjectNumber !== ""
            ? row.ProjectNumber
            : "N/A",
      },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date && row.Date !== ""
            ? dayjs.tz(row.Date).format("M/D/YYYY")
            : "N/A",
      },
      {
        field: "Client",
        valueGetter: (row) =>
          row.Client && row.Client?.Account ? row.Client?.Account : "N/A",
      },
      {
        field: "Requestor",
        valueGetter: (row) => (row.Requestor ? row.Requestor.Name : "N/A"),
      },
      {
        field: "TimeStart",
        valueGetter: (row) => {
          const data = row.TimeStart;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "TimeEnd",
        valueGetter: (row) => {
          const data = row.TimeEnd;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "Duration",
        valueGetter: (row) => {
          const data = row.Duration;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      { field: "Details", valueGetter: (row) => row.Details || "N/A" },
      {
        field: "InterpretationStatus",
        valueGetter: (row) => row.InterpretationStatus || "N/A",
      },
      {
        field: "AccountCharge",
        valueGetter: (row) => {
          const data = row.AccountCharge;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      {
        field: "TotalCost",
        valueGetter: (row) => {
          const data = row.TotalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "LinguistPay",
        valueGetter: (row) => {
          const data = row.LinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "TotalLinguistPay",
        valueGetter: (row) => {
          const data = row.TotalLinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "AdditionalCost",
        valueGetter: (row) => {
          const data = row.AdditionalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "ServiceClientRateSimultaneous",
        valueGetter: (row) => {
          const data = row.ServiceClientRateSimultaneous;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "RateSimultaneous",
        valueGetter: (row) => {
          const data = row.RateSimultaneous;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "EquipmentCost",
        valueGetter: (row) => {
          const data = row.EquipmentCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Linguist",
        valueGetter: (row) => (row.Linguist ? row.Linguist.Name : "N/A"),
      },
      {
        field: "TargetLanguage",
        valueGetter: (row) => {
          const languages = row.ServiceTargetLanguages;
          if (!languages || languages.length === 0) return "N/A";
          return languages
            .map((item) =>
              item.Language && item.Language.Name ? item.Language.Name : "",
            )
            .filter((name) => name)
            .join(" | ");
        },
      },
      {
        field: "Profit",
        valueGetter: (row) => {
          const data = row.Profit;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Margin",
        valueGetter: (row) => {
          const data = row.Margin;
          if (data !== undefined && data !== null) {
            const percentage = data * 1;
            if (percentage === 0) return "0%";
            if (percentage === 100) return "100%";
            return `${percentage.toFixed(2)}%`;
          }
          return "N/A";
        },
      },
      {
        field: "InterpretationType",
        valueGetter: (row) => row.ServiceSubtype?.Name || "N/A",
      },
      { field: "Address", valueGetter: (row) => row.Address || "N/A" },
      {
        field: "MileageRoundtrip",
        valueGetter: (row) => {
          const data = row.MileageRoundtrip;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "MileageCost",
        valueGetter: (row) => {
          const data = row.MileageCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "ParkingFeesTolls",
        valueGetter: (row) => {
          const data = row.ParkingFeesTolls;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "PreparationTime",
        valueGetter: (row) => {
          const data = row.PreparationTime;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      {
        field: "SchedulingTime",
        valueGetter: (row) => {
          const data = row.SchedulingTime;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      {
        field: "ActualStart",
        valueGetter: (row) => {
          const data = row.ActualStart;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "ActualEnd",
        valueGetter: (row) => {
          const data = row.ActualEnd;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "TotalDuration",
        valueGetter: (row) => {
          const data = row.TotalDuration;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      {
        field: "Rate",
        valueGetter: (row) => {
          const data = row.Rate;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Cancelled",
        valueGetter: (row) => {
          const data = row.Cancelled;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CancellationReason",
        valueGetter: (row) =>
          row.CancellationReason && row.CancellationReason !== ""
            ? row.CancellationReason
            : "N/A",
      },
      {
        field: "Invoiced",
        valueGetter: (row) => {
          const data = row.Invoiced;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CreatedBy",
        valueGetter: (row) => (row.Creator ? row.Creator.Name : "N/A"),
      },
      {
        field: "CheckedBy",
        valueGetter: (row) => (row.Checker ? row.Checker.Name : "N/A"),
      },
      {
        field: "CheckedOn",
        valueGetter: (row) =>
          row.CheckingDate
            ? dayjs.tz(row.CheckingDate).format("M/D/YYYY")
            : "N/A",
      },
      {
        // 2026-10-13 — Checks filter buckets (Passed / Warnings / Errors).
        // APPENDED LAST (index 38) on purpose: the six desktop headers that
        // read `filterData[0..5]` literals depend on this array's order, so
        // inserting at the grid position would shift every index.
        field: "Checks",
        valueGetter: (row) => getChecksBucket(row),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterColumns is deliberately frozen (deps [] since inception); its Checks valueGetter follows the render-captured-closure flow documented at the checkConfig effect (~L2436). Adding deps would change cross-filter refresh timing — out of scope for this hygiene pass.
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return listOfServices || [];
      }

      return (listOfServices || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, listOfServices, filterColumns]);

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(
      CONST.INTERPRETATION_FILTER_KEY,
      JSON.stringify(newFilterModel),
    );
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem(
      CONST.INTERPRETATION_SORT_KEY,
      JSON.stringify(newSortModel),
    );
  };

  let totalAccountCharge = 0;
  let totalTotalCost = 0;
  let totalProfit = 0;

  const filteredRowIdsSet = new Set((filterRowIds || []).map(String));
  const includeAll = filteredRowIdsSet.size === 0;

  // Use columnFilteredData for totals calculation to include column filter effects
  const dataForTotals = columnFilteredData || [];

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    totalAccountCharge += Number(service.AccountCharge);
    totalTotalCost += Number(service.TotalCost);
    totalProfit += Number(service.Profit);
  });

  const listOfTotals = [
    { name: "Total Account Charge", total: totalAccountCharge },
    { name: "Total Total Cost", total: totalTotalCost },
    { name: "Total Profit", total: totalProfit },
  ];

  // Count statuses for Members
  let completedCount = 0;
  let confirmedCount = 0;
  let requestedCount = 0;
  let cancelledCount = 0;

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    const status = service.InterpretationStatus;
    if (status === "Completed") completedCount++;
    else if (status === "Confirmed") confirmedCount++;
    else if (status === "Requested") requestedCount++;
    else if (service.Cancelled) cancelledCount++;
  });

  const listOfServiceStatuses = [
    { name: "Completed", count: completedCount },
    { name: "Confirmed", count: confirmedCount },
    { name: "Requested", count: requestedCount },
    { name: "Cancelled", count: cancelledCount },
  ];

  // State for toggling between totals and statuses
  // Check localStorage first, then fall back to role-based default (Admin: totals, Member: statuses)
  const getInitialShowTotals = () => {
    const saved = localStorage.getItem(CONST.INTERPRETATIONS_SHOW_TOTALS_KEY);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return currentUser?.role !== "Admin";
  };

  const [showTotals, setShowTotals] = useState(getInitialShowTotals);

  // Save to localStorage when toggling
  const handleToggleShowTotals = () => {
    const newValue = !showTotals;
    setShowTotals(newValue);
    localStorage.setItem(
      CONST.INTERPRETATIONS_SHOW_TOTALS_KEY,
      JSON.stringify(newValue),
    );
  };

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  // Grid Options Overlay Component
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={!columns.some((col) => columnsVisible[col.field] === false)}
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No interpretations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services
              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              const csvRows = dataToExport
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            // Check if this field has a special mapping
                            const mappedPath = fieldPaths[col.field];
                            if (mappedPath) {
                              value = getFieldValue(row, mappedPath);
                            } else if (col.field === "TargetLanguage") {
                              // Special handling for TargetLanguage (complex nested array)
                              const languages = row.ServiceTargetLanguages;
                              if (languages && Array.isArray(languages)) {
                                value = languages
                                  .map((tl) => tl.Language?.Name || "")
                                  .join(" | ");
                              }
                            } else if (col.field === "Checks") {
                              // 2026-10-08 — No raw row field; mirror the
                              // column's valueGetter ("OK" / "N issue(s)")
                              // so the export is never blank for Checks.
                              value = getChecksSummaryValue(row);
                            } else {
                              value = getFieldValue(row, col.field);
                              if (value === "" && col.field in row) {
                                value = row[col.field];
                              }
                            }
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `interpretations-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={async () => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No interpretations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              showSnackbar("Preparing XLSX export…", theme, "info");

              // Create workbook and worksheet
              const ExcelJS = await loadExcelJS();
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Interpretations");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              dataToExport.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      const mappedPath = fieldPaths[col.field];
                      if (mappedPath) {
                        value = getFieldValue(row, mappedPath);
                      } else if (col.field === "TargetLanguage") {
                        const languages = row.ServiceTargetLanguages;
                        if (languages && Array.isArray(languages)) {
                          value = languages
                            .map((tl) => tl.Language?.Name || "")
                            .join(" | ");
                        }
                      } else if (col.field === "Checks") {
                        // 2026-10-08 — mirror the column's valueGetter.
                        value = getChecksSummaryValue(row);
                      } else {
                        value = getFieldValue(row, col.field);
                        if (value === "" && col.field in row) {
                          value = row[col.field];
                        }
                      }
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });


              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `interpretations-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
              showSnackbar(
                "Couldn't prepare the XLSX export — check your connection and try again.",
                theme,
                "error",
              );
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  // Export dialog bridge — keep the dialog factory fresh at click time. The
  // export MenuItem lives inside a memoized toolbar, so it captures
  // <ExportDialogContent /> from the render where that memo last recomputed —
  // which can predate the data load (stale closure → the dialog says
  // "No data available to export"). This ref is reassigned every commit, so
  // the click always opens the latest render's dialog with current data.
  useEffect(() => {
    openExportDialogRef.current = () =>
      openDialog({
        title: "Export Data",
        theme,
        customContent: <ExportDialogContent />,
      });
  });

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
      selectionEnabled: toolbarSelectionEnabled,
      selectionModel: toolbarSelectionModel,
      allSelectedChecked: toolbarAllSelectedChecked,
      onCancelSelection,
      onMarkChecked,
      onAddToCalendar,
      cardMode,
      quickFilterValue,
      onQuickFilterChange,
    }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      const buttons = [];

      if (toolbarSelectionEnabled) {
        buttons.push({
          label: "Cancel Selection",
          onClick: () => {
            onCancelSelection();
            handleClose();
          },
          icon: null,
        });
        buttons.push({
          label: "Mark As Checked",
          onClick: () => {
            onMarkChecked();
            handleClose();
          },
          icon: <TaskAltRoundedIcon />,
          disabled: toolbarSelectionModel.length === 0,
          isChecked: toolbarAllSelectedChecked,
        });
        // Only show Add To Calendar for admins
        if (currentUser?.role === "Admin") {
          buttons.push({
            label: "Add To Calendar",
            onClick: () => {
              onAddToCalendar();
              handleClose();
            },
            icon: <NotificationsActiveRoundedIcon />,
            disabled: toolbarSelectionModel.length === 0,
          });
        }
      }

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobile && toolbarSelectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TaskAltRoundedIcon />}
                disabled={toolbarSelectionModel.length === 0}
                onClick={onMarkChecked}
                sx={{
                  mr: 1,
                  color: toolbarAllSelectedChecked
                    ? toolbarTheme.palette.text.disabled
                    : toolbarTheme.palette.primary.main,
                  borderColor: toolbarAllSelectedChecked
                    ? toolbarTheme.palette.action.disabled
                    : toolbarTheme.palette.primary.main,
                  "&:hover": toolbarAllSelectedChecked
                    ? {
                        borderColor: toolbarTheme.palette.action.disabled,
                        backgroundColor: "transparent",
                        color: toolbarTheme.palette.text.disabled,
                      }
                    : {
                        borderColor:
                          toolbarTheme.palette.primary.buttonHoverSelected,
                        backgroundColor:
                          toolbarTheme.palette.primary.buttonHoverSelected,
                        color: toolbarTheme.palette.background.default,
                      },
                }}
              >
                Mark As Checked
              </Button>
              {currentUser?.Role === "Admin" && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<NotificationsActiveRoundedIcon />}
                  disabled={toolbarSelectionModel.length === 0}
                  onClick={onAddToCalendar}
                  sx={{
                    mr: 1,
                    color: toolbarTheme.palette.primary.main,
                    borderColor: toolbarTheme.palette.primary.main,
                    "&:hover": {
                      borderColor:
                        toolbarTheme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        toolbarTheme.palette.primary.buttonHoverSelected,
                      color: toolbarTheme.palette.background.default,
                    },
                  }}
                >
                  Add To Calendar
                </Button>
              )}
            </>
          )}

          {isMobile && buttons.length > 0 && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  {buttons.map((btn, index) => (
                    <MenuItem
                      key={index}
                      onClick={btn.onClick}
                      disabled={btn.disabled}
                      sx={{
                        color: btn.isChecked
                          ? theme.palette.text.disabled
                          : theme.palette.primary.main,
                      }}
                    >
                      {btn.icon && (
                        <ListItemIcon
                          sx={{
                            color: btn.isChecked
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                          }}
                        >
                          {btn.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText>{btn.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Box>
              </Popover>
            </>
          )}

          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {toolbarHasFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.alt,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobile && toolbarHasFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            {cardMode ? (
              <QuickFilterField
                iconOnly
                value={quickFilterValue}
                onChange={onQuickFilterChange}
              />
            ) : (
              <CEQuickFilter iconOnly={isMobile} />
            )}

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: toolbarTheme.palette.grey.main,
                  borderColor: toolbarTheme.palette.divider,
                  border: `1px solid ${toolbarTheme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: toolbarTheme.palette.primary.main,
                    //backgroundColor: toolbarTheme.palette.primary.buttonHoverSelected,
                    color: toolbarTheme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openExportDialogRef.current?.();
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [theme, isMobile, currentUser?.Role, currentUser?.role]);

  // Batch C wave 4 — hoisted out of customFooter so the SAME element tree
  // renders in both the desktop grid footer and card mode (mobile.footer).
  const totalsStrip = (
    <Box
      sx={{
        p: "1rem",
        display: "flex",
        flexWrap: "wrap", // wrap to new line if too long
        gap: 2, // spacing between items
        alignItems: "center",
      }}
    >
      <IconButton
        onClick={handleToggleShowTotals}
        size="small"
        sx={{
          color: theme.palette.primary.main,
          "&:hover": {
            color: theme.palette.primary.buttonHoverSelected,
          },
        }}
      >
        <SwapHorizRoundedIcon fontSize="small" />
      </IconButton>

      {showTotals
        ? listOfTotals.map((total) => {
            return (
              <Box key={`${total}-${total.name}`}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexWrap: "wrap", justifyContent: "center" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {total.name}:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme?.palette?.secondary?.[400],
                      fontWeight: 600,
                    }}
                  >
                    ${Number(total.total).toLocaleString()}
                  </Typography>
                </Stack>
              </Box>
            );
          })
        : listOfServiceStatuses.map((status) => {
            return (
              <Box key={`${status}-${status.name}`}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexWrap: "wrap", justifyContent: "center" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {status.name}:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme?.palette?.secondary?.[400],
                      fontWeight: 600,
                    }}
                  >
                    {status.count}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
    </Box>
  );

  const customFooter = () => {
    return (
      <GridFooterContainer>
        {/* Add what you want here */}

        {totalsStrip}

        <GridFooter
          sx={{
            border: "none", // remove double border
          }}
        />
      </GridFooterContainer>
    );
  };

  // ── Batch C wave 4 — card-mode filter sheet ────────────────────────────────
  // One entry per visible column, built from the exact same props as its
  // desktop header (DataGridHeader → ColumnFilter / ColumnDateFilter) so filter
  // semantics are identical between modes. In card mode the desktop headers are
  // not rendered, so these sheet copies are the only live filter instances.
  const buildMobileFilterNode = (field) => {
    const colDef = filteredColumns.find((col) => col.field === field);
    const dataIndex = filterColumns.findIndex((col) => col.field === field);
    const FilterComponent = CARD_MODE_DATE_FILTER_FIELDS.includes(field)
      ? ColumnDateFilter
      : ColumnFilter;
    return (
      // The header filters keep their trigger at opacity 0 until the column
      // header is hovered — impossible inside a touch sheet, so the sheet copy
      // forces the trigger visible (the desktop headers are untouched).
      <Box sx={{ "& .MuiIconButton-root": { opacity: 1 } }}>
        <FilterComponent
          data={
            CARD_MODE_FILTER_DATA_FIELDS.includes(field)
              ? filterData[dataIndex] || []
              : listOfServices || []
          }
          field={field}
          valueGetter={colDef?.valueGetter}
          selectedValues={columnFilters[field] || []}
          onFilterChange={(values) =>
            handleColumnFilterChangeWithStorage(field, values)
          }
          // 2026-10-13 — non-sortable columns (Checks) get no sort UI in the
          // sheet, matching the desktop header where onSortChange is omitted.
          onSortChange={
            colDef?.sortable === false
              ? undefined
              : (sortField, direction) => {
                  const newSortModel = direction
                    ? [{ field: sortField, sort: direction }]
                    : [];
                  setSortModel(newSortModel);
                  handleSortModelChange(newSortModel);
                }
          }
          currentSort={
            colDef?.sortable === false
              ? null
              : sortModel.find((s) => s.field === field)?.sort || null
          }
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
        />
      </Box>
    );
  };

  const mobileFilterEntries = filteredColumns
    // 2026-10-13 — the Checks column is now filterable and included in the
    // sheet; the skip list is empty but kept as the mechanism for future
    // informational columns.
    .filter((col) => !CARD_MODE_NON_FILTER_FIELDS.includes(col.field))
    .map((col) => {
      const selected = columnFilters[col.field] || [];
    return {
      field: col.field,
      headerName: col.headerName || col.field,
      summary: selected.length > 0 ? `${selected.length} selected` : "All",
      active: selected.length > 0,
      node: buildMobileFilterNode(col.field),
    };
  });

  const mobileActiveFilterCount = mobileFilterEntries.filter(
    (entry) => entry.active,
  ).length;

  // Chips row (§3b): one removable chip per active column filter, plus a
  // trailing "Clear all" chip when 2+ filters are active (same semantics as
  // the sheet / toolbar clear). The wrapper adds the search chip itself.
  const mobileFilterChips = mobileFilterEntries
    .filter((entry) => entry.active)
    .map((entry) => ({
      key: `column-filter-${entry.field}`,
      label: `${entry.headerName}: ${columnFilters[entry.field].length} selected`,
      onClear: () => handleColumnFilterChangeWithStorage(entry.field, []),
    }));

  if (mobileFilterChips.length >= 2) {
    mobileFilterChips.push({
      key: "column-filters-clear-all",
      label: "Clear all",
      onClear: () => clearAllFiltersRef.current(),
    });
  }

  // 2026-10-08 — Derived values for the mini profile side panel (desktop
  // single-click). Formatting mirrors the grid column valueGetters so the
  // panel never disagrees with the row it was opened from.
  const sidePanelValues = useMemo(() => {
    if (!sidePanelRow) return null;

    const formatDate = (data) =>
      data && data !== "" ? dayjs.tz(data).format("M/D/YYYY") : "N/A";

    const formatTime = (data) => {
      if (data === "00:00:00") return "12:00 AM";
      if (data && data !== "") {
        return dayjs.tz(data, "HH:mm:ss", "America/New_York").format("h:mm A");
      }
      return "N/A";
    };

    const formatDuration = (data) => {
      if (data && data !== "00:00:00") {
        const [hours, minutes] = data.split(":");
        return `${hours}:${minutes}`;
      }
      return "N/A";
    };

    const formatMoney = (data, useLocale = false) => {
      if (data === undefined || data === null || data === "") return "N/A";
      if (parseFloat(data) === 0) return "N/A";
      return useLocale ? `$${parseFloat(data).toLocaleString()}` : `$${data}`;
    };

    const formatMargin = (data) => {
      if (data === undefined || data === null) return "N/A";
      const percentage = data * 1;
      if (percentage === 0) return "0%";
      if (percentage === 100) return "100%";
      return `${percentage.toFixed(2)}%`;
    };

    const formatMileage = (miles, cost) => {
      const milesPart =
        miles !== undefined && miles !== null && parseFloat(miles) > 0
          ? `${miles} mi`
          : null;
      const costPart =
        cost !== undefined && cost !== null && parseFloat(cost) > 0
          ? `$${cost}`
          : null;
      if (!milesPart && !costPart) return "N/A";
      return [milesPart, costPart].filter(Boolean).join(" · ");
    };

    const languageNames = (sidePanelRow.ServiceTargetLanguages || [])
      .map((item) => (item.Language && item.Language.Name) || "")
      .filter(Boolean)
      .join(" | ");

    const contacts = (
      Array.isArray(sidePanelRow.ContactPersons)
        ? sidePanelRow.ContactPersons
        : []
    )
      .map((contact, index) => ({
        key: contact?.id ?? `contact-${index}`,
        name: contact?.Name || "",
        phone: normalizePhoneNumber(contact?.PhoneNumber),
        email: (contact?.Email || "").trim() || null,
      }))
      .filter((contact) => contact.name || contact.phone !== "N/A");

    const emailThreadIds = parseEmailThreadIds(sidePanelRow.EmailThreadID);

    const checks = runInterpretationChecks(sidePanelRow, {
      mileageRates,
      linguistsById,
      checkConfig,
    });

    // Phone resolution is the module-scope resolveRecordPhone (2026-10-13:
    // lifted out of this memo so the footer ⋮ menu shares the single
    // source).
    const linguistRecord = sidePanelRow.Linguist || null;
    const linguistBrief =
      linguistRecord &&
      linguistRecord.id !== null &&
      linguistRecord.id !== undefined
        ? linguistsById[linguistRecord.id]
        : null;
    // "N/A" linguist rows (canonical id 1) must never show quick actions.
    const linguistNotApplicable =
      Number(linguistRecord?.id) === 1 ||
      String(linguistRecord?.Name || "")
        .trim()
        .toUpperCase() === "N/A";
    // The list payload's linguist include does not load PhoneNumbers, so the
    // /linguists/brief record (which does) is the reliable phone source.
    const linguistPhone =
      resolveRecordPhone(linguistRecord) || resolveRecordPhone(linguistBrief);
    const linguistEmail =
      (linguistRecord?.Email || "").trim() ||
      (linguistBrief?.Email || "").trim() ||
      null;

    const requestorPhone = resolveRecordPhone(sidePanelRow.Requestor);
    const invoicerPhone = resolveRecordPhone(sidePanelRow.Invoicer);

    // 2026-10-12 (Amine) — copy / Open in Maps actions on the Address row.
    // The actions only render for a real value (not "", null or "N/A") —
    // the same no-dead-button guard style as the contact actions above.
    const rawAddress =
      typeof sidePanelRow.Address === "string"
        ? sidePanelRow.Address.trim()
        : "";
    const hasAddress =
      rawAddress !== "" && rawAddress.toUpperCase() !== "N/A";

    return {
      projectNumber:
        sidePanelRow.ProjectNumber && sidePanelRow.ProjectNumber !== ""
          ? sidePanelRow.ProjectNumber
          : "N/A",
      status: sidePanelRow.InterpretationStatus || "N/A",
      date: formatDate(sidePanelRow.Date),
      timeStart: formatTime(sidePanelRow.TimeStart),
      timeEnd: formatTime(sidePanelRow.TimeEnd),
      duration: formatDuration(sidePanelRow.Duration),
      mode:
        sidePanelRow.Mode ||
        sidePanelRow.LocationType ||
        sidePanelRow.ServiceMode ||
        "N/A",
      // 2026-10-12 — every desktop grid column is represented in the panel.
      // These mirror the grid column valueGetters verbatim.
      interpretationType:
        sidePanelRow.ServiceSubtype?.Name &&
        sidePanelRow.ServiceSubtype.Name !== ""
          ? sidePanelRow.ServiceSubtype.Name
          : "N/A",
      actualStart: formatTime(sidePanelRow.ActualStart),
      actualEnd: formatTime(sidePanelRow.ActualEnd),
      totalDuration: formatDuration(sidePanelRow.TotalDuration),
      preparationTime: formatDuration(sidePanelRow.PreparationTime),
      schedulingTime: formatDuration(sidePanelRow.SchedulingTime),
      client: sidePanelRow.Client?.Account || "N/A",
      requestor: sidePanelRow.Requestor?.Name || "N/A",
      invoicer: sidePanelRow.Invoicer?.Name || "N/A",
      linguist: sidePanelRow.Linguist?.Name || "N/A",
      language: languageNames || "N/A",
      address: sidePanelRow.Address || "N/A",
      // Raw (trimmed) address for the copy / maps actions; null when the
      // value is not a real address so nothing misleading gets copied.
      addressRaw: hasAddress ? rawAddress : null,
      hasAddress,
      details: sidePanelRow.Details || "N/A",
      accountCharge: formatMoney(sidePanelRow.AccountCharge, true),
      totalCost: formatMoney(sidePanelRow.TotalCost),
      totalLinguistPay: formatMoney(sidePanelRow.TotalLinguistPay),
      linguistPay: formatMoney(sidePanelRow.LinguistPay),
      additionalCost: formatMoney(sidePanelRow.AdditionalCost),
      additionalLinguistCost: formatMoney(sidePanelRow.AdditionalLinguistCost),
      equipmentCost: formatMoney(sidePanelRow.EquipmentCost),
      parkingFees: formatMoney(sidePanelRow.ParkingFeesTolls),
      linguistRate: formatMoney(sidePanelRow.Rate),
      linguistSimultaneousRate: formatMoney(sidePanelRow.RateSimultaneous),
      clientSimultaneousRate: formatMoney(
        sidePanelRow.ServiceClientRateSimultaneous,
      ),
      mileage: formatMileage(
        sidePanelRow.MileageRoundtrip,
        sidePanelRow.MileageCost,
      ),
      profit: formatMoney(sidePanelRow.Profit),
      margin: formatMargin(sidePanelRow.Margin),
      cancelled:
        sidePanelRow.Cancelled === undefined ||
        sidePanelRow.Cancelled === null
          ? "N/A"
          : sidePanelRow.Cancelled
            ? "Yes"
            : "No",
      cancellationReason:
        sidePanelRow.CancellationReason &&
        sidePanelRow.CancellationReason !== ""
          ? sidePanelRow.CancellationReason
          : "N/A",
      invoiced:
        sidePanelRow.Invoiced === undefined || sidePanelRow.Invoiced === null
          ? "N/A"
          : sidePanelRow.Invoiced
            ? "Yes"
            : "No",
      createdBy: sidePanelRow.Creator ? sidePanelRow.Creator.Name : "N/A",
      checkedBy: sidePanelRow.Checker ? sidePanelRow.Checker.Name : "N/A",
      checkedOn: formatDate(sidePanelRow.CheckingDate),
      // Quick-action data — render shows buttons only for what exists.
      linguistProfileId: linguistNotApplicable
        ? null
        : linguistRecord?.id || null,
      linguistPhone: linguistNotApplicable ? null : linguistPhone,
      linguistEmail: linguistNotApplicable ? null : linguistEmail,
      clientProfileId: sidePanelRow.Client?.id || null,
      requestorPhone,
      requestorEmail: (sidePanelRow.Requestor?.Email || "").trim() || null,
      invoicerPhone,
      invoicerEmail: (sidePanelRow.Invoicer?.Email || "").trim() || null,
      contacts,
      emailThreadIds,
      checks,
    };
  }, [sidePanelRow, mileageRates, linguistsById, checkConfig]);

  // 2026-10-13 (Amine) — data for the footer ⋮ menu's Communications items
  // (mirrors the Assignment Profile's right column), resolved from the
  // selected list row + the linguist brief cache exactly like the side
  // panel's quick-action data. Every entry is null when unusable so the menu
  // renders only items that exist (no dead actions).
  const panelMenuComms = useMemo(() => {
    if (!panelMenuRow) return null;

    const linguistRecord = panelMenuRow.Linguist || null;
    const linguistBrief =
      linguistRecord &&
      linguistRecord.id !== null &&
      linguistRecord.id !== undefined
        ? linguistsById[linguistRecord.id]
        : null;
    // "N/A" linguist rows (canonical id 1) must never show contact actions
    // — same guard as the side panel's quick-action data.
    const linguistNotApplicable =
      Number(linguistRecord?.id) === 1 ||
      String(linguistRecord?.Name || "")
        .trim()
        .toUpperCase() === "N/A";
    const linguistPhone = linguistNotApplicable
      ? null
      : resolveRecordPhone(linguistRecord) ||
        resolveRecordPhone(linguistBrief);
    const linguistEmail = linguistNotApplicable
      ? null
      : (linguistRecord?.Email || "").trim() ||
        (linguistBrief?.Email || "").trim() ||
        null;

    // Check Mileage gate — profile rules verbatim: a linguist is assigned,
    // both addresses exist, the destination is not a meeting link, and the
    // type is drivable (the address falls back to the /linguists/brief
    // record, mirroring the panel's phone fallback).
    const linguistAddress = String(
      linguistRecord?.Address || linguistBrief?.Address || "",
    ).trim();
    const serviceAddress = String(panelMenuRow.Address || "").trim();
    const isLink =
      /^https?:\/\/|^(www\.)|zoom\.|teams\.|meet\.google\.com/.test(
        serviceAddress.toLowerCase(),
      );
    const subtypeName = String(
      panelMenuRow.ServiceSubtype?.Name ||
        panelMenuRow.InterpretationType ||
        "",
    )
      .trim()
      .toLowerCase();
    const isNonDrivableType = /virtual|video|on-demand|phone|opi/.test(
      subtypeName,
    );
    const canCheckMileage = Boolean(
      linguistRecord &&
        linguistAddress &&
        serviceAddress &&
        !isLink &&
        !isNonDrivableType,
    );

    // Per-contact Call items — same mapping shape as the panel's contacts
    // helper (normalizePhoneNumber), phone required so no dead buttons.
    const contacts = (
      Array.isArray(panelMenuRow.ContactPersons)
        ? panelMenuRow.ContactPersons
        : []
    )
      .map((contact, index) => ({
        key: contact?.id ?? `panel-menu-contact-${index}`,
        name: (contact?.Name || "").trim(),
        phone: normalizePhoneNumber(contact?.PhoneNumber),
      }))
      .filter(
        (contact) =>
          contact.phone !== "N/A" && String(contact.phone).trim() !== "",
      );

    return {
      linguistName: (linguistRecord?.Name || "").trim() || "Linguist",
      linguistPhone,
      linguistEmail,
      linguistAddress,
      serviceAddress,
      canCheckMileage,
      requestorName: (panelMenuRow.Requestor?.Name || "").trim() || "Requestor",
      requestorPhone: resolveRecordPhone(panelMenuRow.Requestor),
      requestorEmail: (panelMenuRow.Requestor?.Email || "").trim() || null,
      contacts,
    };
  }, [panelMenuRow, linguistsById]);

  // 2026-10-08 — Profit tint for the side panel Costs section (display-only;
  // the formatted sidePanelValues string carries no sign information).
  const sidePanelProfitNumber =
    sidePanelRow &&
    sidePanelRow.Profit !== null &&
    sidePanelRow.Profit !== undefined &&
    sidePanelRow.Profit !== ""
      ? Number(sidePanelRow.Profit)
      : NaN;
  const sidePanelProfitColor =
    !Number.isFinite(sidePanelProfitNumber) || sidePanelProfitNumber === 0
      ? theme.palette.text.secondary
      : sidePanelProfitNumber > 0
        ? theme.palette.success.main
        : theme.palette.error.main;

  return (
    <Box
      //borderRadius="1.5rem 1.5rem 0 0"
      p="1rem 1rem 0 1rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`, // Adjust to take full viewport height minus margin
        // 100dvh where supported: bottom controls stay clear of iOS Safari's collapsing chrome
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Calendar Sync Progress Overlay */}
      {calendarSyncProgress.isOpen && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            backgroundColor: theme.palette.background.paper,
            borderRadius: "8px",
            p: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            minWidth: "300px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Adding to Calendar
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: theme.palette.text.secondary }}
          >
            {calendarSyncProgress.current} / {calendarSyncProgress.total}
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: "8px",
              backgroundColor: theme.palette.action.disabled,
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                backgroundColor: theme.palette.primary.main,
                width: `${(calendarSyncProgress.current / calendarSyncProgress.total) * 100}%`,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Overlay Backdrop */}
      {calendarSyncProgress.isOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.3)",
            zIndex: 9998,
          }}
        />
      )}

      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid size={{ xs: 12 }} sx={{ px: 1 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  flex: isMobile ? 0 : 1,
                  maxWidth: isMobile ? undefined : FILTER_SELECTOR_WIDTH,
                }}
              >
                <CustomDateSelector
                  label="Date Range"
                  months={months}
                  years={
                    yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                  }
                  selectedMonths={tempSelectedMonths}
                  selectedYears={tempSelectedYear}
                  onSave={handleDateSelectionSave}
                  isMultiMonth={true}
                  isMultiYear={false}
                  monthGridItemSize={3}
                  yearGridItemSize={4}
                  showClearFilter={false}
                  iconOnly={isMobile}
                />
              </Box>

              <Box display="flex" gap={2} alignItems="center">
                {!isMobile ? (
                  <>
                    <Button
                      onClick={() => {
                        setOverlayName(OVERLAY_NAMES.INTERPRETATION_REMINDERS);
                        setOverlayData({});
                      }}
                      startIcon={<NotificationsActiveRoundedIcon />}
                      sx={{
                        width: "200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px 16px",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.primary.main,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          marginRight: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>Reminders</Box>
                    </Button>

                    <Button
                      onClick={() => {
                        setPendingPreAssign(true);
                      }}
                      startIcon={<PersonSearchRoundedIcon />}
                      sx={{
                        width: "200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px 16px",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.primary.main,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          marginRight: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        Pre-Assign Outreach
                      </Box>
                    </Button>

                    <Button
                      onClick={() => {
                        setPendingCreateNew(true);
                      }}
                      startIcon={<AddRoundedIcon />}
                      sx={{
                        width: "200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px 16px",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.background.default,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          marginRight: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                          color: theme.palette.background.default,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        Add Interpretation
                      </Box>
                    </Button>
                  </>
                ) : (
                  <>
                    <Tooltip title="Reminders">
                      <IconButton
                        onClick={() => {
                          setOverlayName(
                            OVERLAY_NAMES.INTERPRETATION_REMINDERS,
                          );
                          setOverlayData({});
                        }}
                        sx={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: theme.palette.background.default,
                          boxShadow:
                            "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                          borderRadius: "12px",
                          border: "none",
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.buttonHover,
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        <NotificationsActiveRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      onClick={() => {
                        setPendingPreAssign(true);
                      }}
                      sx={{
                        width: "40px",
                        minWidth: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.primary.main,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <PersonSearchRoundedIcon />
                    </Button>

                    <Button
                      onClick={() => {
                        setPendingCreateNew(true);
                      }}
                      sx={{
                        width: "40px",
                        minWidth: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.background.default,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        "& .MuiButton-endIcon": {
                          display: "none",
                        },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                          color: theme.palette.background.default,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <AddRoundedIcon />
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        ref={sidePanelGridRef}
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          position: "relative",
          boxShadow: theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGridResponsive
          localeText={{
            noRowsLabel: "No interpretation projects added yet for this month.",
            noResultsOverlayLabel: "No matches for your filters",
          }}
          loading={isLoading}
          mobile={{
            // Batch C wave 4 — card mode (mobile) config. The desktop grid
            // ignores this prop (the wrapper drops it before rendering).
            // ProjectNumber is the field managers use to reference an
            // interpretation (unique id, first column); Status carries the
            // tinted status chip from its own renderCell.
            primaryField: "ProjectNumber",
            badgeField: "InterpretationStatus",
            // §8 audit: no action columns, no interactive cells (the ⋯ menu
            // is right-click only; the ✓/✗ icons are read-only).
            hiddenFields: [],
            // Long free-text / composite columns never read as one line.
            multilineFields: [
              "Details",
              "Address",
              "TargetLanguage",
              "CancellationReason",
            ],
            pageSize: 20,
            // Long-press selection + bulk actions exist on this screen.
            selectable: true,
            // 2026-10-05 (Amine): mobile has no right-click — the card's
            // top-right ⋮ replays this screen's context menu (same handler,
            // same items and disabled states).
            contextMenuButton: true,
            // Card-mode filter sheet: one entry per visible column, reusing
            // the exact header ColumnFilter / ColumnDateFilter props;
            // activeCount drives the toolbar badge; Clear all reuses the
            // toolbar's own clear (column filters + storage + quick filter).
            filters: mobileFilterEntries,
            activeCount: mobileActiveFilterCount,
            onClearFilters: () => clearAllFiltersRef.current(),
            // The same element tree as the desktop grid footer (hoisted above
            // into `totalsStrip`).
            footer: totalsStrip,
            // Removable chips under the control row (the wrapper adds the
            // search chip itself).
            chips: mobileFilterChips,
          }}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          disableColumnSorting
          onStateChange={(e) => {
            const lookup = e.filter?.filteredRowsLookup;
            if (!lookup) return;

            const nextIds = Object.keys(lookup).filter((key) => lookup[key]);
            nextIds.sort();

            const prevIds = prevFilteredRowIdsRef.current;
            const isSame =
              prevIds.length === nextIds.length &&
              prevIds.every((id, idx) => id === nextIds[idx]);

            if (!isSame) {
              prevFilteredRowIdsRef.current = nextIds;
              setFilterRowIds(nextIds);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          apiRef={apiRef}
          density={density}
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            // A long-press opens a 700ms suppression WINDOW synchronously,
            // while the `selectionEnabled` state update may not have rendered
            // yet; the click that follows the long-press must neither toggle
            // selection nor open the detail. Outside the window the gate is
            // structurally closed (round 7: boolean → timestamp + single-
            // shot reset) — a stale guard can never eat a plain tap.
            if (Date.now() < suppressClickUntilRef.current) {
              event.defaultMuiPrevented = true;
              suppressClickUntilRef.current = 0;
              return;
            }

            if (selectionEnabled) {
              // Armed: the wrapper's auto-toggle handles this tap's selection.
              return;
            }

            // Blanket card rule 2026-10-05 (Amine) — one tap opens the
            // interpretation detail exactly like the desktop double-click
            // (desktop keeps click = highlight, double-click = open).
            if (isMobile) {
              navigate(`/interpretations/${params.row.id}`, {
                state: buildProfileNavigationState(params.row),
              });
              return;
            }

            setSelectionModel([params.id]);
            // 2026-10-12 (Amine) — the panel footer's Edit and ⋮ actions
            // reuse the context-menu handlers, which read `selectedRow`;
            // keep it in lockstep with the row the panel shows.
            setSelectedRow(params.id);
            // 2026-10-08 (Amine) — desktop single-click also opens the mini
            // profile side panel for the tapped row; clicking another row
            // swaps the panel content. Mobile tap-through above is unchanged.
            setSidePanelRowId(params.id);
          }}
          onRowDoubleClick={(params) => {
            if (openFilter || selectionEnabled) return;

            navigate(`/interpretations/${params.row.id}`, {
              state: buildProfileNavigationState(params.row),
            });
          }}
          getCellClassName={(params) => {
            return params.value === "No" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar, footer: customFooter }}
          columns={filteredColumns}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: () => clearAllFiltersRef.current(),
              theme,
              selectionEnabled,
              selectionModel,
              allSelectedChecked: selectionStats.allChecked,
              onCancelSelection: handleCancelSelection,
              onMarkChecked: handleMarkSelectedAsChecked,
              onAddToCalendar: handleAddSelectedToCalendar,
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",
            height: "100%", // Make DataGrid take full height of container
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />

        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={handleContextMenuEdit}
          >
            <ListItemIcon>
              <EditRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Edit
            </ListItemText>
          </MenuItem>

          {canCompleteSelected && (
            <MenuItem
              onMouseEnter={handleStatusMenuClose}
              onClick={handleContextMenuComplete}
            >
              <ListItemIcon>
                <TaskRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.primary.text }}>
                Complete
              </ListItemText>
            </MenuItem>
          )}

          <MenuItem
            onMouseEnter={handleStatusMenuOpen}
            onMouseLeave={handleStatusMenuLeave}
            onClick={(event) => event.stopPropagation()}
          >
            <ListItemIcon>
              <SwapHorizRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Status
            </ListItemText>
            <ChevronRightRoundedIcon fontSize="small" />
          </MenuItem>

          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={() => {
              handleContextMenuPreAssignOutreach();
            }}
          >
            <ListItemIcon>
              <PersonSearchRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Pre-Assign Outreach
            </ListItemText>
          </MenuItem>

          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={handleContextMenuDuplicate}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Duplicate
            </ListItemText>
          </MenuItem>

          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={handleContextMenuAddTask}
          >
            <ListItemIcon>
              <AddTaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Add Task
            </ListItemText>
          </MenuItem>

          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={handleContextMenuAddNote}
          >
            <ListItemIcon>
              <CommentRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Add Note
            </ListItemText>
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={handleContextMenuCallRequestor}
          >
            <ListItemIcon>
              <CallRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Call{" "}
              {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
                ?.Name || "Requestor"}
            </ListItemText>
          </MenuItem>

          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={handleContextMenuEmailRequestor}
          >
            <ListItemIcon>
              <EmailRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Email{" "}
              {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
                ?.Name || "Requestor"}
            </ListItemText>
          </MenuItem>
        </Menu>

        <Popper
          open={Boolean(statusMenuAnchor)}
          anchorEl={statusMenuAnchor}
          placement="right-start"
          sx={{ zIndex: theme.zIndex.modal + 1 }}
        >
          <Paper
            elevation={8}
            onMouseEnter={handleStatusMenuEnter}
            onMouseLeave={handleStatusMenuLeave}
            sx={{
              minWidth: 150,
              py: 1,
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            }}
          >
            {statusOptions.map((status) => (
              <MenuItem
                key={status}
                onClick={() => handleContextMenuStatusChange(status)}
              >
                <ListItemIcon>
                  {status === currentStatus ? (
                    <CheckRoundedIcon fontSize="small" />
                  ) : (
                    <span style={{ width: 20 }} />
                  )}
                </ListItemIcon>
                <ListItemText sx={{ color: theme.palette.primary.text }}>
                  {status}
                </ListItemText>
              </MenuItem>
            ))}
          </Paper>
        </Popper>

        {/* 2026-10-08 (Amine) — mini profile side panel: desktop single-click
            on a row docks this panel INSIDE the grid container so it hovers
            over the DataGrid only. No backdrop / no Modal: clicking outside
            (or on another row) leaves it open and swaps its content; it
            closes via X, auto-closes when bulk selection arms, and when the
            row leaves the list. Resizable from its left edge.
            2026-10-08 (Amine): desktop-only render + breakpoint-responsive
            default width; manual drag persists as an override; double-click
            resets. */}
        {!isMobile && sidePanelRow && sidePanelValues && (
          <Box
            ref={sidePanelRef}
            role="complementary"
            aria-label="Interpretation quick view"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: effectiveSidePanelWidth,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderRadius: "0 12px 12px 0",
              bgcolor: theme.palette.background.alt,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "-8px 0 24px rgba(0,0,0,0.45)"
                  : "-8px 0 24px rgba(0,0,0,0.08)",
            }}
          >
            {/* Drag handle on the panel's left edge — drag left/right to
                resize; double-click resets to the responsive default. */}
            <Box
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panel — drag to resize, double-click to reset"
              title="Drag to resize — double-click to reset"
              onPointerDown={handleSidePanelResizeStart}
              onDoubleClick={() => {
                // 2026-10-08 (Amine) — drops the manual override so the
                // width snaps back to the breakpoint-responsive default.
                setSidePanelWidth(null);
                localStorage.removeItem(SIDE_PANEL_WIDTH_STORAGE_KEY);
              }}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 8,
                zIndex: 2,
                cursor: "col-resize",
                touchAction: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& .sidePanelResizePill": {
                  width: 3,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: "divider",
                  transition: "background-color 120ms ease",
                },
                "&:hover .sidePanelResizePill": {
                  bgcolor: theme.palette.primary.main,
                },
                ...(isSidePanelResizing && {
                  "& .sidePanelResizePill": {
                    bgcolor: theme.palette.primary.main,
                  },
                }),
              }}
            >
              <Box className="sidePanelResizePill" />
            </Box>

            {/* Status accent strip */}
            <Box
              sx={{
                height: 3,
                flexShrink: 0,
                bgcolor: getStatusColor(theme, sidePanelValues.status),
              }}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{
                px: 2,
                pl: 2.5,
                py: 1.25,
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  noWrap
                  sx={{ fontSize: "1.05rem", fontWeight: 700 }}
                >
                  {sidePanelValues.projectNumber}
                </Typography>
                <Box
                  sx={{
                    borderRadius: "12px",
                    px: "0.625rem",
                    py: "0.2rem",
                    backgroundColor: getStatusColor(
                      theme,
                      sidePanelValues.status,
                    ),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    maxWidth: 170,
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: theme.palette.background.default,
                      fontSize: "0.78rem",
                      fontWeight: 600,
                    }}
                  >
                    {sidePanelValues.status}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                size="small"
                aria-label="Close side panel"
                onClick={() => setSidePanelRowId(null)}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>

            {/* 2026-10-12 (Amine) — Checks sit at the very top of the panel
                (above the money strip) so issues are always visible without
                scrolling. Rendering is unchanged from the previous version. */}
            <Box sx={{ flexShrink: 0 }}>
              <SidePanelSection
                title="Checks"
                icon={<FactCheckRoundedIcon sx={{ fontSize: 16 }} />}
              >
                {sidePanelValues.checks.length === 0 ? (
                  <Box
                    sx={{
                      borderRadius: "10px",
                      px: 1.25,
                      py: 1,
                      bgcolor: (theme) => `${theme.palette.success.main}14`,
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <ChecksSeverityBadge severity="passed" />
                      <Typography
                        sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                      >
                        All checks passed
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  sidePanelValues.checks.map((issue, index) => (
                    <Stack
                      key={`${issue.code}-${index}`}
                      direction="row"
                      spacing={0.75}
                      alignItems="flex-start"
                      sx={{ py: 0.5 }}
                    >
                      {/* Chip language (Amine 2026-10-13): mini severity
                          badges matching the Checks column chip. */}
                      {issue.severity === "error" ? (
                        <ChecksSeverityBadge
                          severity="error"
                          sx={{ mt: "2px" }}
                        />
                      ) : (
                        <ChecksSeverityBadge
                          severity="warning"
                          sx={{ mt: "2px" }}
                        />
                      )}
                      <Typography
                        sx={{
                          minWidth: 0,
                          overflowWrap: "anywhere",
                          fontSize: "0.875rem",
                        }}
                      >
                        {issue.message}
                      </Typography>
                    </Stack>
                  ))
                )}
              </SidePanelSection>
              <Divider sx={{ my: 0.5 }} />
            </Box>

            {/* Money stat strip — quick-glance totals (sidePanelValues only) */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                px: 2,
                pl: 2.5,
                py: 1.25,
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              {[
                {
                  label: "Account Charge",
                  value: sidePanelValues.accountCharge,
                  color: theme.palette.secondary.main,
                },
                {
                  label: "Total Cost",
                  value: sidePanelValues.totalCost,
                  color: theme.palette.primary.main,
                },
                {
                  label: "Total Linguist Pay",
                  value: sidePanelValues.totalLinguistPay,
                  color: theme.palette.text.primary,
                },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "10px",
                    bgcolor: `${theme.palette.primary.main}0D`,
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      lineHeight: 1.25,
                      color: "text.secondary",
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: stat.color,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
              <SidePanelSection
                title="Schedule"
                icon={<AccessTimeRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Start"
                    value={sidePanelValues.timeStart}
                  />
                  <SidePanelCompactField
                    label="End"
                    value={sidePanelValues.timeEnd}
                  />
                  <SidePanelCompactField
                    label="Duration"
                    value={sidePanelValues.duration}
                  />
                  <SidePanelCompactField
                    label="Total Duration"
                    value={sidePanelValues.totalDuration}
                  />
                  <SidePanelCompactField
                    label="Actual Start"
                    value={sidePanelValues.actualStart}
                  />
                  <SidePanelCompactField
                    label="Actual End"
                    value={sidePanelValues.actualEnd}
                  />
                  <SidePanelCompactField
                    label="Preparation Time"
                    value={sidePanelValues.preparationTime}
                  />
                  <SidePanelCompactField
                    label="Scheduling Time"
                    value={sidePanelValues.schedulingTime}
                  />
                  <SidePanelCompactField
                    label="Date"
                    value={sidePanelValues.date}
                  />
                  <SidePanelCompactField
                    label="Mode"
                    value={sidePanelValues.mode}
                  />
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Client Data"
                icon={<PersonRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Client"
                    value={sidePanelValues.client}
                    actions={
                      sidePanelValues.clientProfileId
                        ? [
                            {
                              key: "open",
                              label: "Open Client Profile",
                              Icon: OpenInNewRoundedIcon,
                              onClick: () =>
                                navigate(
                                  `/clients/${sidePanelValues.clientProfileId}`,
                                ),
                            },
                          ]
                        : []
                    }
                  />
                  <SidePanelCompactField
                    label="Requestor"
                    value={sidePanelValues.requestor}
                    actions={buildDirectContactActions(
                      sidePanelValues.requestorPhone,
                      sidePanelValues.requestorEmail,
                    )}
                  />
                  <SidePanelCompactField
                    label="Invoice To"
                    value={sidePanelValues.invoicer}
                    actions={buildDirectContactActions(
                      sidePanelValues.invoicerPhone,
                      sidePanelValues.invoicerEmail,
                    )}
                  />
                  <SidePanelCompactField
                    label="Language"
                    value={sidePanelValues.language}
                  />
                </SidePanelFieldGrid>
                {/* 2026-10-12 (Amine) — Linguist and Address stay
                    single-column full-width: both can be long. */}
                <SidePanelField
                  label="Linguist"
                  value={sidePanelValues.linguist}
                  actions={[
                    ...(sidePanelValues.linguistProfileId
                      ? [
                          {
                            key: "open",
                            label: "Open Linguist Profile",
                            Icon: OpenInNewRoundedIcon,
                            onClick: () =>
                              navigate(
                                `/linguists/${sidePanelValues.linguistProfileId}`,
                              ),
                          },
                        ]
                      : []),
                    ...buildDirectContactActions(
                      sidePanelValues.linguistPhone,
                      sidePanelValues.linguistEmail,
                    ),
                  ]}
                />
                {/* 2026-10-12 (Amine) — Copy + Open in Maps actions; the
                    actions array is empty unless the address is a real
                    value, so no dead buttons ever render. */}
                <SidePanelField
                  label="Address"
                  value={sidePanelValues.address}
                  multiline
                  actions={
                    sidePanelValues.hasAddress
                      ? [
                          {
                            key: "copy",
                            label: "Copy address",
                            Icon: ContentCopyRoundedIcon,
                            onClick: () =>
                              copyAddressToClipboard(
                                sidePanelValues.addressRaw,
                                showSnackbar,
                                theme,
                              ),
                          },
                          {
                            key: "maps",
                            label: "Open in Maps",
                            Icon: PlaceRoundedIcon,
                            onClick: () =>
                              window.open(
                                buildMapsSearchUrl(
                                  sidePanelValues.addressRaw,
                                ),
                                "_blank",
                                "noopener,noreferrer",
                              ),
                          },
                        ]
                      : []
                  }
                />
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Costs"
                icon={<AttachMoneyRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Account Charge"
                    value={sidePanelValues.accountCharge}
                  />
                  <SidePanelCompactField
                    label="Total Cost"
                    value={sidePanelValues.totalCost}
                  />
                  <SidePanelCompactField
                    label="Total Linguist Pay"
                    value={sidePanelValues.totalLinguistPay}
                  />
                  <SidePanelCompactField
                    label="Linguist Pay"
                    value={sidePanelValues.linguistPay}
                  />
                  <SidePanelCompactField
                    label="Additional Cost"
                    value={sidePanelValues.additionalCost}
                  />
                  <SidePanelCompactField
                    label="Additional Linguist Cost"
                    value={sidePanelValues.additionalLinguistCost}
                  />
                  <SidePanelCompactField
                    label="Equipment Cost"
                    value={sidePanelValues.equipmentCost}
                  />
                  <SidePanelCompactField
                    label="Parking Fees"
                    value={sidePanelValues.parkingFees}
                  />
                  <SidePanelCompactField
                    label="Mileage"
                    value={sidePanelValues.mileage}
                  />
                  <SidePanelCompactField
                    label="Profit"
                    value={
                      <Typography
                        sx={{
                          fontSize: "0.9375rem",
                          fontWeight: 600,
                          color: sidePanelProfitColor,
                        }}
                      >
                        {sidePanelValues.profit}
                      </Typography>
                    }
                  />
                  <SidePanelCompactField
                    label="Margin"
                    value={sidePanelValues.margin}
                  />
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              {/* 2026-10-12 (Amine) — Rates: the three rate columns, split
                  like the other short fact rows. */}
              <SidePanelSection
                title="Rates"
                icon={<AttachMoneyRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Linguist Rate"
                    value={sidePanelValues.linguistRate}
                  />
                  <SidePanelCompactField
                    label="Linguist Simultaneous Rate"
                    value={sidePanelValues.linguistSimultaneousRate}
                  />
                  <SidePanelGridFullRow>
                    <SidePanelCompactField
                      label="Client Simultaneous Rate"
                      value={sidePanelValues.clientSimultaneousRate}
                    />
                  </SidePanelGridFullRow>
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              {/* 2026-10-12 (Amine) — Status & Record: type / billing flags /
                  audit trail, mirroring the grid columns' valueGetters. */}
              <SidePanelSection
                title="Status & Record"
                icon={<TaskAltRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Interpretation Type"
                    value={sidePanelValues.interpretationType}
                  />
                  <SidePanelCompactField
                    label="Invoiced"
                    value={sidePanelValues.invoiced}
                  />
                  <SidePanelCompactField
                    label="Cancelled"
                    value={sidePanelValues.cancelled}
                  />
                  <SidePanelCompactField
                    label="Checked On"
                    value={sidePanelValues.checkedOn}
                  />
                  <SidePanelCompactField
                    label="Created By"
                    value={sidePanelValues.createdBy}
                  />
                  <SidePanelCompactField
                    label="Checked By"
                    value={sidePanelValues.checkedBy}
                  />
                  <SidePanelGridFullRow>
                    <SidePanelField
                      label="Cancellation Reason"
                      value={sidePanelValues.cancellationReason}
                      multiline
                    />
                  </SidePanelGridFullRow>
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Details"
                icon={<NotesRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelField
                  label="Details"
                  multiline
                  value={
                    <Typography
                      component="div"
                      sx={{
                        display: "-webkit-box",
                        overflow: "hidden",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        fontSize: "0.9375rem",
                        fontWeight: 500,
                        color: "text.primary",
                        lineHeight: 1.45,
                      }}
                    >
                      {sidePanelValues.details}
                    </Typography>
                  }
                />
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Contact Persons"
                icon={<ContactPhoneRoundedIcon sx={{ fontSize: 16 }} />}
              >
                {sidePanelValues.contacts.length === 0 ? (
                  <Typography
                    sx={{ color: "text.secondary", fontSize: "0.9375rem" }}
                  >
                    No contact persons
                  </Typography>
                ) : (
                  sidePanelValues.contacts.map((contact) => (
                    <Box
                      key={contact.key}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 0,
                          overflowWrap: "anywhere",
                          fontSize: "0.9375rem",
                          fontWeight: 500,
                        }}
                      >
                        {contact.name || "N/A"}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ flexShrink: 0 }}
                      >
                        {contact.phone !== "N/A" && (
                          <Typography
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.875rem",
                            }}
                          >
                            {contact.phone}
                          </Typography>
                        )}
                        {/* 2026-10-12 (Amine) — per-contact quick actions. */}
                        <SidePanelActions
                          actions={buildDirectContactActions(
                            contact.phone,
                            contact.email,
                          )}
                        />
                      </Stack>
                    </Box>
                  ))
                )}
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Email Threads"
                icon={<EmailRoundedIcon sx={{ fontSize: 16 }} />}
              >
                {sidePanelValues.emailThreadIds.length === 0 ? (
                  <Typography
                    sx={{ color: "text.secondary", fontSize: "0.9375rem" }}
                  >
                    N/A
                  </Typography>
                ) : (
                  <Stack spacing={0.5} alignItems="flex-start">
                    {sidePanelValues.emailThreadIds.map(
                      (emailThreadId, index) => (
                        <Button
                          key={`${emailThreadId}-${index}`}
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewRoundedIcon fontSize="small" />}
                          onClick={() =>
                            window.open(
                              buildEmailThreadLink(emailThreadId),
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                        >
                          {sidePanelValues.emailThreadIds.length === 1
                            ? "Open Email Thread"
                            : `Open Email Thread ${index + 1}`}
                        </Button>
                      ),
                    )}
                  </Stack>
                )}
              </SidePanelSection>

            </Box>

            <Box
              sx={{
                px: 2,
                py: 1.5,
                flexShrink: 0,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* 2026-10-12 (Amine) — footer trio: Open Profile / Edit / ⋮
                  actions. Open Profile keeps the exact profile navigation
                  payload; Edit reuses the grid context menu's edit handler;
                  the ⋮ menu (2026-10-13) mirrors the Assignment Profile's
                  right-column Actions panel (same sections, labels, icons,
                  gates) so every profile action is reachable from the
                  panel itself. */}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    flex: 1,
                    minHeight: 44,
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                  }}
                  onClick={() =>
                    navigate(`/interpretations/${sidePanelRow.id}`, {
                      state: buildProfileNavigationState(sidePanelRow),
                    })
                  }
                >
                  Open Profile
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<EditRoundedIcon />}
                  sx={{ flexShrink: 0, minHeight: 44 }}
                  onClick={handleContextMenuEdit}
                >
                  Edit
                </Button>
                <Tooltip title="More actions">
                  <IconButton
                    aria-label="More actions"
                    onClick={(event) =>
                      setPanelMenuAnchor(event.currentTarget)
                    }
                    sx={{
                      flexShrink: 0,
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.border}`,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.hover,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MoreVertRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* Footer "More actions" menu — 2026-10-13 (Amine): mirrors the
                Assignment Profile's right-column Actions panel (same
                sections, labels, icons, gates, dialogs and payloads), NOT
                the grid's right-click context menu. Every item also closes
                this menu. Gates recompute from the selected list row. */}
            <Menu
              anchorEl={panelMenuAnchor}
              open={Boolean(panelMenuAnchor)}
              onClose={() => setPanelMenuAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: theme.palette.background.alt,
                    border: `1px solid ${theme.palette.divider}`,
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.main,
                    },
                  },
                },
              }}
            >
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 2,
                }}
              >
                Assignment
              </ListSubheader>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handleContextMenuPreAssignOutreach();
                }}
              >
                <ListItemIcon>
                  <PersonSearchRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Pre-Assign Outreach</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handleContextMenuEdit();
                }}
              >
                <ListItemIcon>
                  <EditRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit Assignment</ListItemText>
              </MenuItem>

              {canCompletePanelSelected && (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handleContextMenuComplete();
                  }}
                >
                  <ListItemIcon>
                    <TaskRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Complete Assignment</ListItemText>
                </MenuItem>
              )}

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelMarkAsChecked();
                }}
              >
                <ListItemIcon>
                  <VerifiedUserRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mark As Checked</ListItemText>
              </MenuItem>

              {canMarkPanelSelectedAsPaid ? (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelMarkAsPaid();
                  }}
                >
                  <ListItemIcon>
                    <CreditScoreRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Mark As Paid</ListItemText>
                </MenuItem>
              ) : canMarkPanelSelectedAsUnpaid ? (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelMarkAsUnpaid();
                  }}
                >
                  <ListItemIcon>
                    <RestartAltRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Mark As Unpaid</ListItemText>
                </MenuItem>
              ) : null}

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelChangeCategory();
                }}
              >
                <ListItemIcon>
                  <CategoryRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Category</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelDeleteAssignment();
                }}
              >
                <ListItemIcon>
                  <DeleteRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete Assignment</ListItemText>
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 2,
                }}
              >
                Tasks & Notes
              </ListSubheader>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handleContextMenuAddTask();
                }}
              >
                <ListItemIcon>
                  <AddTaskRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Task</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handleContextMenuAddNote();
                }}
              >
                <ListItemIcon>
                  <CommentRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Note</ListItemText>
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 2,
                }}
              >
                Communications
              </ListSubheader>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelSendDetails();
                }}
              >
                <ListItemIcon>
                  <ForwardToInboxRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Send Details</ListItemText>
              </MenuItem>

              {panelMenuComms?.canCheckMileage && (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelCheckMileage();
                  }}
                >
                  <ListItemIcon>
                    <RouteRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Check Mileage</ListItemText>
                </MenuItem>
              )}

              {panelMenuComms?.linguistEmail && (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelEmail(panelMenuComms.linguistEmail);
                  }}
                >
                  <ListItemIcon>
                    <EmailRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    Email {panelMenuComms.linguistName}
                  </ListItemText>
                </MenuItem>
              )}

              {panelMenuComms?.linguistPhone && (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelCall(panelMenuComms.linguistPhone);
                  }}
                >
                  <ListItemIcon>
                    <CallRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    Call {panelMenuComms.linguistName}
                  </ListItemText>
                </MenuItem>
              )}

              {panelMenuComms?.requestorEmail && (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelEmail(panelMenuComms.requestorEmail);
                  }}
                >
                  <ListItemIcon>
                    <EmailRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    Email {panelMenuComms.requestorName}
                  </ListItemText>
                </MenuItem>
              )}

              {panelMenuComms?.requestorPhone && (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelCall(panelMenuComms.requestorPhone);
                  }}
                >
                  <ListItemIcon>
                    <CallRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    Call {panelMenuComms.requestorName}
                  </ListItemText>
                </MenuItem>
              )}

              {panelMenuComms?.contacts.map((contact) => (
                <MenuItem
                  key={contact.key}
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelCall(contact.phone);
                  }}
                >
                  <ListItemIcon>
                    <CallRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    Call {contact.name || "Contact"}
                  </ListItemText>
                </MenuItem>
              ))}

              <Divider sx={{ my: 0.5 }} />

              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 2,
                }}
              >
                Utilities
              </ListSubheader>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelCopyTrackerRow();
                }}
              >
                <ListItemIcon>
                  <ContentCopyRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy Tracker Row</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={{
            left: window.innerWidth / 2,
            top: window.innerHeight / 2,
          }}
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              borderRadius: "12px",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.5)"
                  : "0 8px 32px rgba(0,0,0,0.15)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Interpretations;
